import React, { useState, useRef, useEffect } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import * as Tabs from '@radix-ui/react-tabs';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/Textarea';
import {
  Camera, X, Upload, Trash2, Edit, Save,
  FileText, Download, File, Plus, CheckCircle,
  IdCard, BookOpen, Paperclip, Calendar, Image,
  UserCheck, Briefcase, Building, Phone, Mail,
  Key, RefreshCw, Lock, Unlock, User, Clock,
  Badge, CreditCard, AlertTriangle, Heart, AtSign, Settings, Eye, Copy, EyeOff, XCircle, Users, Search, DollarSign, ToggleLeft, ToggleRight, ArrowUp, Network, History, Info,
  KeyRound
} from 'lucide-react';

import { api } from '../services/api';
import { useFeatures } from '../contexts/FeaturesContext';
import { format, parseISO } from 'date-fns';
import ConfirmDialog from './ConfirmDialog';
import ScanBadgeRfid from './ui/ScanBadgeRfid';
import ProtectedComponent from './ProtectedComponent';
import AccesEnseignantEmploye from './accesEnseignant/AccesEnseignantEmploye';
import { usePermissionsFicheEmploye } from '../hooks/usePermissionsFicheEmploye';

// ⚠️ AUCUNE ADRESSE ABSOLUE DANS CET ECRAN, ET SURTOUT PAS « localhost ».
// Les photos et les fichiers employaient ici `http://localhost:3001${chemin}`.
// En developpement le serveur est a cette adresse, donc tout s'affichait ; en
// PRODUCTION, le navigateur du gestionnaire allait chercher l'image SUR SA
// PROPRE MACHINE, ou rien n'ecoute — d'ou l'icone d'image cassee, visible
// seulement en production et jamais chez le developpeur. Releve le 04/10/2026.
//
// Le chemin RELATIF rendu par l'API est la seule forme correcte : il suit le
// domaine de l'ecole d'ou vient la page, quelle qu'elle soit. Une adresse
// absolue, « localhost » comme « www.klasys.com », designe toujours la mauvaise
// machine pour quelqu'un.

// ── LES TÉLÉPHONES : UN NUMÉRO PAR CHAMP ─────────────────────────────────────
// Deux numéros saisis dans un seul champ (« 056985108/065816716 ») faisaient
// refuser toute la fiche par le serveur, badge compris. Depuis le 03/11/2026,
// chaque numéro secondaire a son propre champ (bouton « Ajouter un numéro »),
// et chaque champ suit le contrôle du serveur (employeValidation.js) : il est
// signalé à la saisie et arrêté avant l'envoi.
const FORMAT_TELEPHONE = /^\+?[0-9\s-]{8,}$/;
const MAX_TELEPHONES_SECONDAIRES = 3;
const MESSAGE_TELEPHONE = 'Un seul numéro par champ, de 8 chiffres au moins.';
const telephoneMalForme = (valeur) => {
  const numero = String(valeur || '').trim();
  return Boolean(numero) && !FORMAT_TELEPHONE.test(numero);
};
// Le serveur renvoie TOUS les numéros dans `telephones`, le principal en tête :
// les secondaires sont les autres.
const telephonesSecondairesDe = (employe) => {
  const principal = String(employe?.telephone || '').trim();
  return (Array.isArray(employe?.telephones) ? employe.telephones : [])
    .map((numero) => String(numero || '').trim())
    .filter((numero) => numero && numero !== principal);
};

const EmployeeDetailModal = ({
  employee,
  isOpen,
  onClose,
  onUpdate,
  // Une photo ajoutée ou retirée est déjà enregistrée par sa propre route. Le
  // parent n'a qu'à l'afficher : `onUpdate` renvoyait la fiche ENTIÈRE au
  // serveur, et un champ ancien invalide — un « ° » dans le téléphone — faisait
  // échouer ce second envoi, avec un toast d'erreur après le toast de succès.
  onPhotoChange,
  onShowToast,
  isEditing: initialIsEditing = false,
  roles = [],
  departements = []
}) => {
  // Deux moitiés, deux modules vendus séparément :
  //   la réduction sur les frais scolaires → comptabilité
  //   la retenue sur le salaire            → paie
  // Une école qui facture sans gérer la paie accorde donc la réduction, et
  // l'employé règle le solde comme n'importe quel parent.
  const { estActive } = useFeatures();
  // Un enfant a charge n'est pas un fait comptable : c'est une RETENUE SUR
  // SALAIRE, prelevee au moment de payer le salaire. Sans la paie, on pourrait
  // declarer l'enfant et fixer le montant, et rien ne le prelevrait jamais :
  // l'ecole croirait percevoir la scolarite sans rien percevoir. La section
  // suit donc la paie, pas la comptabilite.
  const enfantsOuvert = estActive('module.paie');
  const retenueSurSalaireOuverte = estActive('module.paie');
  // Le numero de badge ne sert qu'au pointage ; le scan RFID n'a d'usage que
  // par le kiosque, vendu comme extension. Le formulaire de creation faisait
  // deja ce partage — la fiche, elle, laissait voir et modifier les deux.
  const pointageOuvert = estActive('module.pointage');
  const rfidOuvert = estActive('cap.rfid');
  // Onglet « Accès enseignant » : seulement si l'école a ouvert l'espace
  // enseignant. Sans la licence, le serveur refuse /api/acces-enseignant : un
  // onglet visible n'afficherait qu'une erreur.
  const espaceEnseignantOuvert = estActive('module.portail_enseignant');



  // Etat pour enfant à charges 
  const [enfantsACharge, setEnfantsACharge] = useState([]);
  const [searchEleve, setSearchEleve] = useState('');
  const [elevesRecherche, setElevesRecherche] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [selectedEleves, setSelectedEleves] = useState([]);
  const [isLoadingEnfants, setIsLoadingEnfants] = useState(false);
  const [enfantsInterSite, setEnfantsInterSite] = useState([]);
  const [isLoadingEnfantsInterSite, setIsLoadingEnfantsInterSite] = useState(false);


  const [isLoadingConnectionHistory, setIsLoadingConnectionHistory] = useState(false);
  const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  const [isEditing, setIsEditing] = useState(initialIsEditing);

  // Modifier une fiche demande RESSOURCE_HUMAINES.DETAILS.EDIT (le serveur
  // refuse sans elle). La liste peut ouvrir cette fenêtre directement en
  // modification : sans le droit, elle s'ouvre en lecture.
  const droitsFiche = usePermissionsFicheEmploye();
  useEffect(() => {
    if (!droitsFiche.chargement && !droitsFiche.peutModifier && isEditing) setIsEditing(false);
  }, [droitsFiche.chargement, droitsFiche.peutModifier, isEditing]);
  const [localEmployee, setLocalEmployee] = useState({ ...employee });
  const [telephonesSecondaires, setTelephonesSecondaires] = useState(() => telephonesSecondairesDe(employee));
  const [photoPreview, setPhotoPreview] = useState(employee.photo_url || null);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('details');
  const [documents, setDocuments] = useState([]);
  const [isResetPasswordModalOpen, setIsResetPasswordModalOpen] = useState(false);
  const [contactUrgence, setContactUrgence] = useState({
    nom_prenom: '',
    relation: '',
    telephone: '',
    email: '',
    adresse: ''
  });
  const fileInputRef = useRef(null);
  const documentInputRef = useRef(null);

  // Prise de photo avec la caméra de l'appareil — même fonctionnement que le
  // formulaire de création d'un employé et l'inscription d'un élève.
  const [cameraActive, setCameraActive] = useState(false);
  const [cameraError, setCameraError] = useState(null);
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [isGeneratingContract, setIsGeneratingContract] = useState(false);
  const [contractGenerated, setContractGenerated] = useState(false);

  const [downloadConfirmDialog, setDownloadConfirmDialog] = useState({
    isOpen: false,
    documentId: null,
    fileName: null
  });

  const [deleteConfirmDialog, setDeleteConfirmDialog] = useState({
    isOpen: false,
    documentId: null,
    type: null
  });
  const [connectionHistoryPagination, setConnectionHistoryPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    total: 0,
    hasNext: false,
    hasPrev: false
  });


  // État pour le dialog de suppression d'enfant
  const [deleteEnfantDialog, setDeleteEnfantDialog] = useState({
    isOpen: false,
    enfantId: null,
    nomEnfant: ''
  });


  const showSuccessToast = (message) => {
    console.log("Success:", message);
    if (onShowToast) {
      onShowToast({ message, type: 'success' });
    }
  };




  // ============================================================================
  // 🆕 FONCTIONS POUR ENFANTS À CHARGE
  // ============================================================================

  /**
   * Rechercher des élèves
   */
  const handleSearchEleve = async (searchTerm) => {
    setSearchEleve(searchTerm);

    if (searchTerm.length < 2) {
      setElevesRecherche([]);
      return;
    }

    setIsSearching(true);

    try {
      // Le serveur cherche dans l'année active, quelle que soit l'année envoyée :
      // un enfant à charge se lie sur la fiche que la paie règle.
      const anneeScolaireId = localStorage.getItem('annee_scolaire_id') || '';
      const response = await api.employes.searchElevesForLiaison(searchTerm, anneeScolaireId);

      if (response.success) {
        setElevesRecherche(response.data);
      } else {
        setError('Erreur lors de la recherche');
      }
    } catch (error) {
      console.error('Erreur recherche élève:', error);
      setError('Erreur lors de la recherche d\'élèves');
    } finally {
      setIsSearching(false);
    }
  };

  /**
   * Sélectionner un élève depuis les résultats de recherche
   */
  const handleSelectEleve = (eleve) => {
    // Vérifier si l'élève n'est pas déjà sélectionné
    if (selectedEleves.find(e => e.id === eleve.id)) {
      setError('Cet élève est déjà sélectionné');
      return;
    }

    // Vérifier si l'élève n'est pas déjà lié
    if (enfantsACharge.find(e => e.eleve_id === eleve.id)) {
      setError('Cet élève est déjà lié à cet employé');
      return;
    }

    setSelectedEleves([...selectedEleves, eleve]);
    setSearchEleve(''); // Réinitialiser la recherche
    setElevesRecherche([]); // Vider les résultats
  };

  /**
   * Retirer un élève de la sélection temporaire
   */
  const handleRemoveFromSelection = (eleveId) => {
    setSelectedEleves(selectedEleves.filter(e => e.id !== eleveId));
  };

  /**
   * Lier les élèves sélectionnés à l'employé
   */
  const handleLierEleves = async () => {
    if (selectedEleves.length === 0) {
      setError('Aucun élève sélectionné');
      return;
    }

    // 🔄 Toast de loading
    if (onShowToast) {
      onShowToast({
        message: `Liaison de ${selectedEleves.length} élève(s) en cours...`,
        type: 'loading'
      });
    }

    try {
      const eleveIds = selectedEleves.map(e => e.id);
      const response = await api.employes.lierEnfantsACharge(employee.id, eleveIds);

      if (response.success) {
        await loadEnfantsACharge(); // Recharger la liste
        setSelectedEleves([]);

        // ✅ Toast de succès
        showSuccessToast(response.message || `${selectedEleves.length} élève(s) lié(s) avec succès`);
      } else {
        // ❌ Toast d'erreur
        if (onShowToast) {
          onShowToast({
            message: response.message || 'Erreur lors de la liaison',
            type: 'error'
          });
        }
        setError(response.message);
      }
    } catch (error) {
      console.error('Erreur liaison enfants:', error);

      // ❌ Toast d'erreur
      if (onShowToast) {
        onShowToast({
          message: 'Erreur lors de la liaison des enfants',
          type: 'error'
        });
      }
      setError('Erreur lors de la liaison des enfants');
    }
  };

  /**
   * Activer/désactiver la déduction automatique
   */
  const handleToggleDeduction = async (enfantId) => {
    // Trouver l'enfant pour savoir si on active ou désactive
    const enfant = enfantsACharge.find(e => e.id === enfantId);
    const isActivating = !enfant?.deduction_active;

    // 🔄 Toast de loading
    if (onShowToast) {
      onShowToast({
        message: isActivating
          ? 'Activation de la déduction automatique en cours...'
          : 'Désactivation de la déduction automatique en cours...',
        type: 'loading'
      });
    }

    try {
      const response = await api.employes.toggleDeductionEnfant(employee.id, enfantId);

      if (response.success) {
        // Mettre à jour localement
        setEnfantsACharge(enfantsACharge.map(enfant =>
          enfant.id === enfantId
            ? { ...enfant, deduction_active: response.data.deduction_active ? 1 : 0 }
            : enfant
        ));

        // ✅ Toast de succès
        showSuccessToast(response.message || (isActivating
          ? 'Déduction automatique activée avec succès'
          : 'Déduction automatique désactivée avec succès'));
      } else {
        // ❌ Toast d'erreur
        if (onShowToast) {
          onShowToast({
            message: response.message || 'Erreur lors de la modification',
            type: 'error'
          });
        }
        setError(response.message);
      }
    } catch (error) {
      console.error('Erreur toggle déduction:', error);

      // ❌ Toast d'erreur
      if (onShowToast) {
        onShowToast({
          message: 'Erreur lors de la modification de la déduction',
          type: 'error'
        });
      }
      setError('Erreur lors de la modification');
    }
  };


  /**
   * Supprimer une liaison (délier un enfant)
   */
  /**
   * Ouvrir le dialog de confirmation avant suppression
   */
  const handleDelierEnfant = (enfantId) => {
    // Trouver l'enfant pour un message personnalisé
    const enfant = enfantsACharge.find(e => e.id === enfantId);
    const nomEnfant = enfant ? `${enfant.eleve_prenom} ${enfant.eleve_nom}` : 'cet élève';

    // Ouvrir le dialog de confirmation
    setDeleteEnfantDialog({
      isOpen: true,
      enfantId: enfantId,
      nomEnfant: nomEnfant
    });
  };


  /**
   * Exécuter la suppression après confirmation
   */
  const executeDelierEnfant = async () => {
    const { enfantId, nomEnfant } = deleteEnfantDialog;

    // 🔄 Toast de loading
    if (onShowToast) {
      onShowToast({
        message: `Suppression de la liaison avec ${nomEnfant} en cours...`,
        type: 'loading'
      });
    }

    try {
      const response = await api.employes.delierEnfantACharge(employee.id, enfantId);

      if (response.success) {
        setEnfantsACharge(enfantsACharge.filter(e => e.id !== enfantId));

        // ✅ Toast de succès
        showSuccessToast(response.message || `Liaison avec ${nomEnfant} supprimée avec succès`);

        // Fermer le dialog
        setDeleteEnfantDialog({ isOpen: false, enfantId: null, nomEnfant: '' });
      } else {
        // ❌ Toast d'erreur
        if (onShowToast) {
          onShowToast({
            message: response.message || 'Erreur lors de la suppression',
            type: 'error'
          });
        }
        setError(response.message);

        // Fermer le dialog
        setDeleteEnfantDialog({ isOpen: false, enfantId: null, nomEnfant: '' });
      }
    } catch (error) {
      console.error('Erreur suppression liaison:', error);

      // ❌ Toast d'erreur
      if (onShowToast) {
        onShowToast({
          message: 'Erreur lors de la suppression de la liaison',
          type: 'error'
        });
      }
      setError('Erreur lors de la suppression');

      // Fermer le dialog
      setDeleteEnfantDialog({ isOpen: false, enfantId: null, nomEnfant: '' });
    }
  };



  /**
   * Charger les enfants à charge au montage
   */
  const loadEnfantsACharge = async () => {
    setIsLoadingEnfants(true);
    setIsLoadingEnfantsInterSite(true);

    try {
      // 1. Charger les enfants locaux
      const response = await api.employes.getEnfantsACharge(employee.id);
      if (response.success) {
        setEnfantsACharge(response.data);
      }
    } catch (error) {
      console.error('Erreur chargement enfants locaux:', error);
    } finally {
      setIsLoadingEnfants(false);
    }

    // 2. Charger les enfants inter-sites (appel séparé, non bloquant)
    try {
      const responseIS = await api.intersite.getEnfantsInterSite(employee.id);
      if (responseIS.success && responseIS.data) {
        setEnfantsInterSite(responseIS.data);
      }
    } catch (error) {
      console.warn('⚠️ Inter-sites non disponible:', error.message);
      setEnfantsInterSite([]);
    } finally {
      setIsLoadingEnfantsInterSite(false);
    }
  };


  /**
   * Calculer le total des retenues actives
   */
  const calculerTotalRetenues = () => {
    // Retenues locales (actives uniquement)
    const totalLocal = enfantsACharge
      .filter(enfant => enfant.deduction_active === 1)
      .reduce((total, enfant) => total + parseFloat(enfant.frais_mensuel || 0), 0);

    // Retenues inter-sites (toujours actives par définition)
    const totalInterSite = enfantsInterSite
      .reduce((total, enfant) => total + parseFloat(enfant.montant_mensuel || 0), 0);

    return totalLocal + totalInterSite;
  };


  // Charger les enfants à charge quand le modal s'ouvre
  useEffect(() => {
    if (isOpen && employee?.id) {
      loadEnfantsACharge();
    }
  }, [isOpen, employee?.id]);




  const handleConfirmAction = async () => {
    if (confirmAction) {
      try {
        const response = await api.employes.markRejectionAsRead(localEmployee.id);
        if (response.success) {
          // Recharger les données d'accès
          const updatedAccessData = await api.employes.getAccessData(localEmployee.id);
          if (updatedAccessData.success) {
            setAccessData(updatedAccessData);
          }
          // Optionnel : afficher un message de succès
          showSuccessToast('Refus marqué comme traité avec succès');
        }
      } catch (error) {
        console.error('Erreur lors de la mise à jour:', error);
        setError('Erreur lors de la mise à jour');
      }
    }
  };


  useEffect(() => {
    console.log("Données brutes reçues de l'API:", employee);

    // Formatter correctement la date pour le champ input type="date"
    const dateEmbauche = employee.date_embauche;
    const dateNaissance = employee.date_naissance;
    const formattedDateEmbauche = formatDateForInput(dateEmbauche);
    const formattedDateNaissance = formatDateForInput(dateNaissance);

    // Vérifier si l'employé a accès à l'application
    const hasAppAccess = employee.info_pro_acces_application === 1 ||
      employee.info_pro_acces_application === true ||
      employee.acces_application === 1 ||
      employee.acces_application === true;

    setLocalEmployee({
      ...employee,
      date_embauche: formattedDateEmbauche,
      date_naissance: formattedDateNaissance,
      // Standardiser les noms de champs pour faciliter l'accès
      identifiant: employee.identifiant || '',
      acces_application: hasAppAccess,
      departement_nom: employee.departement_nom || 'Non assigné',
      numero_badge: employee.numero_badge || '',
      rfid_uid: employee.rfid_uid || ''
    });
    setTelephonesSecondaires(telephonesSecondairesDe(employee));

    // Charger les documents
    if (employee.documents) {
      setDocuments(employee.documents || []);
    }

    // Charger les contacts d'urgence s'ils existent
    if (employee.contact_urgence) {
      setContactUrgence(employee.contact_urgence);
    } else {
      // Réinitialiser avec des valeurs vides si aucun contact d'urgence
      setContactUrgence({
        nom_prenom: '',
        relation: '',
        telephone: '',
        email: '',
        adresse: ''
      });
    }

    setPhotoPreview(employee.photo_url || null);
  }, [employee, isOpen]);



  useEffect(() => {
    // Cette fonction vérifie si l'URL de la photo est valide
    const checkPhotoUrl = async (url) => {
      if (!url) return;

      try {
        // Ajout d'un timestamp pour éviter les problèmes de cache
        const timeStampedUrl = `${url}?t=${Date.now()}`;
        const response = await fetch(timeStampedUrl, { method: 'HEAD' });

        if (!response.ok) {
          console.warn(`L'URL de photo n'est pas valide: ${url}`);
          setPhotoPreview(null);
        } else {
          setPhotoPreview(`${url}?t=${Date.now()}`);
        }
      } catch (error) {
        console.error('Erreur lors de la vérification de l\'URL de la photo:', error);
        setPhotoPreview(null);
      }
    };

    // Vérifier l'URL de la photo chaque fois que l'employé change
    if (employee.photo_url) {
      checkPhotoUrl(employee.photo_url);
    } else {
      setPhotoPreview(null);
    }
  }, [employee]);


  // Fonction de formatage de date
  const formatDate = (dateString) => {
    if (!dateString) return 'Non renseigné';

    try {
      // Si la date est au format YYYY-MM-DD
      if (typeof dateString === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
        // Utiliser parseISO qui est plus fiable pour les dates ISO
        const parsedDate = parseISO(dateString);
        // Formater en DD/MM/YYYY
        return format(parsedDate, 'dd/MM/yyyy');
      }

      // Pour les dates avec timestamp (2025-03-13T23:00:00.000Z)
      if (typeof dateString === 'string' && dateString.includes('T')) {
        const parsedDate = parseISO(dateString);
        return format(parsedDate, 'dd/MM/yyyy');
      }

      // Essayer de parser d'autres formats
      const parsedDate = new Date(dateString);
      if (!isNaN(parsedDate.getTime())) {
        return format(parsedDate, 'dd/MM/yyyy');
      }
    } catch (e) {
      console.error("Erreur lors du formatage de la date:", e);
    }

    return 'Format de date invalide';
  };


  const getEmployeeRolesDetails = () => {
    const rolesDetails = Array.isArray(localEmployee.roles_details) ? localEmployee.roles_details : [];
    if (rolesDetails.length > 0) return rolesDetails;

    const roleName = localEmployee.poste_metier_nom || localEmployee.role_nom;
    const groupName = localEmployee.groupe_nom || localEmployee.role_nom;
    if (!roleName && !groupName) return [];

    return [{
      role_nom: roleName || 'Non renseign\u00e9',
      groupe_nom: groupName || 'Non renseign\u00e9',
      est_role_principal: true,
      mode_remuneration: localEmployee.mode_remuneration || localEmployee.type_contrat || localEmployee.info_pro_type_contrat,
      salaire_base: localEmployee.salaire_base || localEmployee.salaire || 0,
      categorie_rh_libelle: localEmployee.categorie_rh_libelle || 'Autres',
      classes_horaires: []
    }];
  };

  const formatFcfa = (value) => {
    const amount = Number(value || 0);
    return amount.toLocaleString('fr-FR', { maximumFractionDigits: 0 }) + ' FCFA';
  };

  const getProfilRemunerationLabel = () => {
    const profil = localEmployee.profil_remuneration || '';
    if (profil === 'HYBRIDE') return 'Hybride';
    if (profil === 'HORAIRE') return 'Horaire';
    if (profil === 'MENSUEL') return 'Mensuel';
    return 'Non d\u00e9fini';
  };

  const getRoleModeLabel = (mode) => {
    const normalizedMode = String(mode || '').toUpperCase();
    if (normalizedMode === 'HORAIRE') return 'Horaire';
    if (normalizedMode === 'MENSUEL' || normalizedMode === 'FIXE') return 'Mensuel';
    return mode || 'Non d\u00e9fini';
  };

  const getRoleBadgeClass = (role) => (
    role.est_role_principal
      ? 'bg-[#246990]/10 text-[#246990] border-[#246990]/20'
      : 'bg-[#319190]/10 text-[#319190] border-[#319190]/20'
  );

  const calculateProcessingDelay = (dateDebut, dateFin) => {
    if (!dateDebut || !dateFin) return null;

    const debut = new Date(dateDebut);
    const fin = new Date(dateFin);
    const diffMs = fin - debut;

    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffMinutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));

    if (diffHours > 0) {
      return `${diffHours}h ${diffMinutes}min`;
    } else {
      return `${diffMinutes}min`;
    }
  };


  // Formater la date et heure de création
  const formatCreationDateTime = (dateString) => {
    if (!dateString) return 'Non renseigné';

    try {
      let date;
      if (typeof dateString === 'string') {
        date = parseISO(dateString);
      } else {
        date = new Date(dateString);
      }

      if (isNaN(date.getTime())) return 'Date invalide';

      return format(date, 'dd/MM/yyyy à HH:mm:ss');
    } catch (e) {
      console.error("Erreur lors du formatage de la date de création:", e);
      return 'Format de date invalide';
    }
  };

  // Fonction améliorée pour formater la date pour le champ input
  const formatDateForInput = (dateString) => {
    if (!dateString) return '';

    try {
      // Si c'est déjà au format YYYY-MM-DD, le retourner tel quel
      if (typeof dateString === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
        return dateString;
      }

      // Pour les dates avec timestamp (2025-03-13T23:00:00.000Z)
      if (typeof dateString === 'string' && dateString.includes('T')) {
        const parsedDate = parseISO(dateString);
        return format(parsedDate, 'yyyy-MM-dd');
      }

      // Pour d'autres formats
      const parsedDate = new Date(dateString);
      if (!isNaN(parsedDate.getTime())) {
        return format(parsedDate, 'yyyy-MM-dd');
      }
    } catch (e) {
      console.error("Erreur lors du formatage de la date pour le champ input:", e);
    }

    return '';
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    if (name === 'date_embauche' || name === 'date_naissance') {
      // Pour les dates, on utilise exactement la valeur fournie par le champ date
      console.log("Date brute saisie:", value);
      setLocalEmployee(prev => ({
        ...prev,
        [name]: value,
      }));
    } else if (name === 'departement_id' || name === 'role_id') {
      // Pour les sélecteurs, convertir en nombre
      setLocalEmployee(prev => ({
        ...prev,
        [name]: value ? parseInt(value, 10) : '',
      }));
    } else if (name === 'numero_badge') {
      // Validation pour le numéro de badge (numérique uniquement)
      if (value === '' || /^\d+$/.test(value)) {
        setLocalEmployee(prev => ({
          ...prev,
          [name]: value
        }));
      }
    } else if (name.startsWith('contact_urgence.')) {
      // Gestion des champs du contact d'urgence
      const fieldName = name.split('.')[1];
      setContactUrgence(prev => ({
        ...prev,
        [fieldName]: value
      }));
    } else {
      setLocalEmployee(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };



  const handleDownloadDocument = (docId, docName) => {
    // Ouvrir le dialog de confirmation
    setDownloadConfirmDialog({
      isOpen: true,
      documentId: docId,
      fileName: docName
    });
  };

  const handleDeleteDocument = async (docId, type) => {
    // Ouvrir le dialog de confirmation au lieu de window.confirm
    setDeleteConfirmDialog({
      isOpen: true,
      documentId: docId,
      type: type
    });
  };

  const executeDownloadDocument = async () => {
    const { documentId, fileName } = downloadConfirmDialog;

    try {
      const result = await api.employes.downloadDocument(employee.id, documentId);
      if (!result.success) {
        setError('Erreur lors du téléchargement du document');
      } else {
        showSuccessToast('Téléchargement initialisé');
      }
    } catch (error) {
      console.error('Erreur téléchargement:', error);
      setError('Erreur lors du téléchargement');
    }

    // Fermer le dialog
    setDownloadConfirmDialog({
      isOpen: false,
      documentId: null,
      fileName: null
    });
  };


  const executeDeleteDocument = async () => {
    const { documentId, type } = deleteConfirmDialog;

    // 🔄 Toast de loading
    if (onShowToast) {
      onShowToast({
        message: 'Document en cours de suppression...',
        type: 'loading'
      });
    }

    try {
      const result = await api.employes.deleteDocument(employee.id, documentId);

      if (result.success) {
        // Retirer le document de la liste locale
        setDocuments(documents.filter(doc => doc.id !== documentId));

        // ✅ Toast de succès
        showSuccessToast('Document supprimé avec succès');
      } else {
        // ❌ Toast d'erreur
        if (onShowToast) {
          onShowToast({
            message: 'Erreur lors de la suppression',
            type: 'error'
          });
        }
        setError('Erreur lors de la suppression');
      }
    } catch (error) {
      console.error('Erreur suppression:', error);

      // ❌ Toast d'erreur
      if (onShowToast) {
        onShowToast({
          message: 'Erreur lors de la suppression',
          type: 'error'
        });
      }
      setError('Erreur lors de la suppression');
    } finally {
      // Fermer le dialog
      setDeleteConfirmDialog({
        isOpen: false,
        documentId: null,
        type: null
      });
    }
  };


  const handleSave = async () => {
    setError(null);
    try {
      // Un numéro mal formé est arrêté ici, avant le serveur, en disant lequel.
      const secondaires = telephonesSecondaires
        .map((numero) => String(numero || '').trim())
        .filter(Boolean);
      const malFormes = [localEmployee.telephone, ...secondaires].filter(telephoneMalForme);
      if (malFormes.length > 0) {
        throw new Error(`Numéro de téléphone invalide (${malFormes.join(', ')}) : ${MESSAGE_TELEPHONE.toLowerCase()}`);
      }

      // Préparer les données au format correct pour l'API
      const updateData = {
        id: localEmployee.id,
        nom: localEmployee.nom,
        prenom: localEmployee.prenom,
        email: localEmployee.email,
        telephone: localEmployee.telephone,
        // TOUS les numéros, le principal en tête — la forme que le serveur
        // renvoie. Sans cette liste, chaque enregistrement réécrivait
        // employe_telephones avec le seul principal : les numéros secondaires
        // saisis à la création disparaissaient.
        telephones: [String(localEmployee.telephone || '').trim(), ...secondaires].filter(Boolean),
        adresse: localEmployee.adresse,
        date_naissance: localEmployee.date_naissance,
        lieu_naissance: localEmployee.lieu_naissance || '',
        sexe: localEmployee.sexe || 'M',
        departement_id: parseInt(localEmployee.departement_id, 10) || null,
        date_embauche: localEmployee.date_embauche,
        numero_badge: localEmployee.numero_badge || '',
        // Le badge n'est envoyé QUE s'il a changé.
        //
        // L'envoyer systématiquement ouvrait un piège : ouvert depuis une liste
        // allégée qui ne porte pas `rfid_uid`, le modal aurait transmis une
        // chaîne vide — et le serveur aurait détaché le badge sans que personne
        // l'ait demandé. Champ absent, le serveur laisse la valeur en place.
        ...(String(localEmployee.rfid_uid || '') !== String(employee?.rfid_uid || '')
          ? { rfid_uid: localEmployee.rfid_uid || '' }
          : {}),
        // `info_pro_acces_application` n'est plus transmis : il est posé par
        // l'octroi d'accès, qui génère les identifiants. L'écrire depuis ce
        // formulaire permettait d'annoncer un accès sans identifiant derrière.
        contact_urgence: contactUrgence,
        photo_url: localEmployee.photo_url
      };

      // Utiliser onUpdate (qui sera reçu comme prop du composant parent)
      if (onUpdate) {
        await onUpdate(updateData);
        setIsEditing(false);
        showSuccessToast('Informations mises à jour avec succès');
      }
    } catch (error) {
      console.error("Erreur lors de l'enregistrement:", error);
      // Le motif du serveur, tel quel (« Numéro de téléphone invalide… ») : la
      // fiche reste en modification pour qu'on corrige le champ. Un message
      // générique laissait deviner, et le succès affiché avant ce correctif
      // laissait croire que tout était enregistré (03/11/2026).
      const motif = error?.message || "Erreur lors de l'enregistrement des modifications";
      setError(motif);
      if (onShowToast) {
        onShowToast({ message: motif, type: 'error' });
      }
    }
  };

  const handleCancel = () => {
    setLocalEmployee({ ...employee });
    setTelephonesSecondaires(telephonesSecondairesDe(employee));
    if (employee.contact_urgence) {
      setContactUrgence(employee.contact_urgence);
    } else {
      setContactUrgence({
        nom_prenom: '',
        relation: '',
        telephone: '',
        email: '',
        adresse: ''
      });
    }
    setIsEditing(false);
  };

  // Les numéros secondaires : un champ chacun, trois au plus.
  const ajouterTelephoneSecondaire = () => {
    setTelephonesSecondaires((prev) => (prev.length < MAX_TELEPHONES_SECONDAIRES ? [...prev, ''] : prev));
  };

  const modifierTelephoneSecondaire = (index, valeur) => {
    setTelephonesSecondaires((prev) => prev.map((numero, i) => (i === index ? valeur : numero)));
  };

  const retirerTelephoneSecondaire = (index) => {
    setTelephonesSecondaires((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSelectPhoto = () => {
    fileInputRef.current?.click();
  };

  // ---------------------------------------------------------------- caméra

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
    setCameraActive(false);
  };

  const startCamera = async () => {
    try {
      setCameraError(null);
      if (!navigator.mediaDevices?.getUserMedia) {
        setCameraError("La caméra n'est pas disponible dans ce navigateur.");
        return;
      }
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'user', width: { ideal: 720 }, height: { ideal: 720 } },
        audio: false
      });
      streamRef.current = stream;
      setCameraActive(true);
      setTimeout(() => {
        if (videoRef.current) videoRef.current.srcObject = stream;
      }, 0);
    } catch (erreur) {
      console.error('Erreur ouverture caméra:', erreur);
      setCameraError("Impossible d'ouvrir la caméra. Vérifiez les autorisations du navigateur.");
    }
  };

  // Recadrage au carré, 720 × 720 en JPG : la photo capturée part ensuite par
  // le même chemin qu'une photo importée.
  const handleCapturePhoto = () => {
    const video = videoRef.current;
    if (!video) return;

    const canvas = document.createElement('canvas');
    const largeur = video.videoWidth || 640;
    const hauteur = video.videoHeight || 480;
    const cote = Math.min(largeur, hauteur);
    canvas.width = 720;
    canvas.height = 720;
    canvas.getContext('2d').drawImage(
      video, Math.max(0, (largeur - cote) / 2), Math.max(0, (hauteur - cote) / 2), cote, cote,
      0, 0, canvas.width, canvas.height
    );

    canvas.toBlob(async (blob) => {
      if (!blob) return;
      const fichier = new window.File([blob], `photo_${Date.now()}.jpg`, { type: 'image/jpeg' });
      setPhotoPreview(URL.createObjectURL(blob));
      stopCamera();
      await uploadPhoto(fichier);
    }, 'image/jpeg', 0.9);
  };

  // La caméra ne reste jamais allumée derrière la fiche : coupée en sortant du
  // mode édition, à la fermeture, et au démontage.
  useEffect(() => {
    if (!isEditing || !isOpen) stopCamera();
  }, [isEditing, isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
  }, []);

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const allowedTypes = ['image/jpeg', 'image/png', 'image/jpg'];
    if (!allowedTypes.includes(file.type)) {
      setError('Format de fichier non pris en charge. Utilisez JPG ou PNG.');
      return;
    }

    if (file.size > 5 * 1024 * 1024) { // 5MB
      setError('La taille de l\'image ne doit pas dépasser 5 MB');
      return;
    }

    // Créer un aperçu
    const reader = new FileReader();
    reader.onload = (event) => {
      setPhotoPreview(event.target.result);
    };
    reader.readAsDataURL(file);

    // Uploader la photo - Encapsulé dans un try/catch supplémentaire
    try {
      await uploadPhoto(file);
    } catch (error) {
      console.error('Erreur lors de l\'upload:', error);
      setIsUploading(false); // Arrêter le chargement en cas d'erreur
      setError(`Erreur lors de l'upload: ${error.message}`);
    }
  };

  // Dans le fichier EmployeeDetailModal.jsx
  // Modifiez la fonction uploadPhoto comme suit:

  // Dans EmployeeDetailModal.jsx
  // Dans la fonction uploadPhoto de EmployeeDetailModal.jsx

  const uploadPhoto = async (file) => {
    setIsUploading(true);
    setError(null);

    const formData = new FormData();
    formData.append('photo', file);

    // 🔄 Toast de loading
    if (onShowToast) {
      onShowToast({
        message: 'Photo en cours d\'ajout...',
        type: 'loading'
      });
    }

    try {
      const response = await api.employes.uploadPhoto(employee.id, formData);

      if (response && response.success) {
        const photoUrl = response.photo_url;
        const updatedEmployee = {
          ...localEmployee,
          photo_url: photoUrl
        };

        setLocalEmployee(updatedEmployee);
        setPhotoPreview(`${photoUrl}?t=${Date.now()}`);

        // La photo est enregistrée : le parent l'affiche, sans rien renvoyer.
        if (onPhotoChange) {
          onPhotoChange(employee.id, photoUrl);
        }

        // ✅ Toast de succès
        showSuccessToast('Photo ajoutée avec succès');
      } else {
        throw new Error(response?.error || 'Erreur lors de l\'upload de la photo');
      }
    } catch (error) {
      console.error('Erreur lors de l\'upload de la photo:', error);

      // ❌ Toast d'erreur
      if (onShowToast) {
        onShowToast({
          message: 'Erreur lors de l\'upload de la photo',
          type: 'error'
        });
      }
      setError('Erreur lors de l\'upload de la photo: ' + error.message);
    } finally {
      setIsUploading(false);
    }
  };



  const handleDeletePhoto = async () => {
    // 🔄 Toast de loading
    if (onShowToast) {
      onShowToast({
        message: 'Photo en cours de suppression...',
        type: 'loading'
      });
    }

    try {
      console.log('Début de la suppression de la photo');
      const response = await api.employes.deletePhoto(employee.id);

      if (response.success) {
        setPhotoPreview(null);

        const updatedEmployee = {
          ...localEmployee,
          photo_url: null
        };
        setLocalEmployee(updatedEmployee);

        // Suppression déjà enregistrée : le parent l'affiche, sans rien renvoyer.
        if (onPhotoChange) {
          onPhotoChange(employee.id, null);
        }

        // ✅ Toast de succès
        showSuccessToast('Photo supprimée avec succès');
      } else {
        throw new Error(response.error || 'Erreur lors de la suppression de la photo');
      }
    } catch (error) {
      console.error('Erreur lors de la suppression de la photo:', error);

      // Même en cas d'erreur, si la réponse HTTP était OK
      if (error.message.includes('JSON') ||
        error.message.includes('Unexpected token') ||
        error.message.includes('Unexpected end')) {
        setPhotoPreview(null);
        const updatedEmployee = { ...localEmployee, photo_url: null };
        setLocalEmployee(updatedEmployee);

        // Informer le composant parent même dans ce cas, sans rien renvoyer
        if (onPhotoChange) {
          onPhotoChange(employee.id, null);
        }

        // ✅ Toast de succès (car la suppression a réussi malgré l'erreur de parsing)
        showSuccessToast('Photo supprimée avec succès');
      } else {
        // ❌ Toast d'erreur (vraie erreur)
        if (onShowToast) {
          onShowToast({
            message: 'Erreur lors de la suppression de la photo',
            type: 'error'
          });
        }
        setError('Erreur lors de la suppression de la photo');
      }
    }
  };



  const handleResetPassword = async () => {
    try {
      setIsResetPasswordModalOpen(false);

      // Appeler l'API de réinitialisation de mot de passe (à implémenter)
      //await api.employes.resetPassword(localEmployee.id);

      showSuccessToast('Le mot de passe a été réinitialisé avec succès');
    } catch (error) {
      console.error('Erreur lors de la réinitialisation du mot de passe:', error);
      setError('Erreur lors de la réinitialisation du mot de passe');
    }
  };


  const handleFileUpload = async (e, type) => {
    const file = e.target.files[0];
    if (!file) return;

    const allowedTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
    if (!allowedTypes.includes(file.type)) {
      setError('Format de fichier non pris en charge. Utilisez PDF, DOC ou DOCX.');
      return;
    }

    if (file.size > 10 * 1024 * 1024) { // 10MB
      setError('La taille du document ne doit pas dépasser 10 MB');
      return;
    }

    const formData = new FormData();
    formData.append('document', file);

    // 🔄 Toast de loading
    if (onShowToast) {
      onShowToast({
        message: 'Document en cours d\'insertion...',
        type: 'loading'
      });
    }

    try {
      const response = await api.employes.uploadDocument(employee.id, formData, type);

      if (response.success) {
        // Actualiser la liste des documents
        const updatedDocuments = [...documents, response.document];
        setDocuments(updatedDocuments);

        // ✅ Toast de succès
        showSuccessToast('Document ajouté avec succès');
      } else {
        // ❌ Toast d'erreur
        if (onShowToast) {
          onShowToast({
            message: response.message || 'Erreur lors de l\'upload du document',
            type: 'error'
          });
        }
        setError(response.message || 'Erreur lors de l\'upload du document');
      }
    } catch (error) {
      console.error('Erreur lors de l\'upload du document:', error);

      // ❌ Toast d'erreur
      if (onShowToast) {
        onShowToast({
          message: 'Erreur lors de l\'upload du document',
          type: 'error'
        });
      }
      setError('Erreur lors de l\'upload du document');
    }
  };


  const generateContract = async () => {
    try {
      setIsGeneratingContract(true);

      // Préparer les données pour la génération du contrat
      const contractData = {
        employee: {
          id: localEmployee.id,
          nom: localEmployee.nom,
          prenom: localEmployee.prenom,
          email: localEmployee.email,
          date_embauche: localEmployee.date_embauche,
          date_naissance: localEmployee.date_naissance,
          adresse: localEmployee.adresse,
          departement: localEmployee.departement_nom,
          info_pro_type_contrat: localEmployee.info_pro_type_contrat,
          // Ajouter le rôle s'il existe
          role_nom: localEmployee.role_nom || 'À définir'
        }
      };

      // Appeler l'API pour générer le contrat
      const response = await api.employes.generateEmployeeContract(contractData);

      if (response.success) {
        showSuccessToast('Contrat généré avec succès');

        // Recharger les documents pour afficher le nouveau contrat
        await loadDocuments();

        // Marquer le contrat comme généré
        setContractGenerated(true);

        // Ouvrir le contrat dans un nouvel onglet
        if (response.fileUrl) {
          window.open(response.fileUrl, '_blank');
        }
      } else {
        setError('Erreur lors de la génération du contrat: ' + (response.message || 'Une erreur est survenue'));
      }
    } catch (error) {
      console.error('Erreur lors de la génération du contrat:', error);
      setError('Erreur lors de la génération du contrat');
    } finally {
      setIsGeneratingContract(false);
    }
  };


  const hasExistingContract = () => {
    return documents.filter(doc => doc.type === 'contrat').length > 0;
  };



  // Charger les documents depuis l'API
  const loadDocuments = async () => {
    if (!employee || !employee.id) return;

    try {
      console.log('📂 Chargement documents employé:', employee.id);

      const response = await api.employees.getEmployeeDocuments(employee.id);

      if (response && response.success && response.documents) {
        setDocuments(response.documents);
        console.log('✅', response.documents.length, 'document(s) chargé(s)');
      } else {
        setDocuments([]);
        console.log('ℹ️ Aucun document');
      }
    } catch (error) {
      console.error('❌ Erreur chargement documents:', error);
      setDocuments([]);
    }
  };


  // Fonction pour obtenir le type MIME d'un document
  const getDocumentIcon = (doc) => {
    if (!doc || !doc.nom_fichier) return <File size={30} className="text-gray-500" />;

    if (doc.nom_fichier.endsWith('.pdf') || doc.type === 'application/pdf') {
      return <FileText size={30} className="text-red-500" />;
    } else if (doc.nom_fichier.match(/\.(jpg|jpeg|png|gif)$/i) || doc.type?.startsWith('image/')) {
      return <Image size={30} className="text-blue-500" />;
    } else if (doc.nom_fichier.match(/\.(doc|docx)$/i)) {
      return <FileText size={30} className="text-blue-700" />;
    } else {
      return <File size={30} className="text-gray-500" />;
    }
  };

  // Modal de confirmation pour réinitialiser le mot de passe
  const ResetPasswordModal = ({ isOpen, onClose, onConfirm }) => (
    <Dialog.Root open={isOpen} onOpenChange={() => { }}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/50 z-50" />
        <Dialog.Content className="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-white rounded-xl p-6 max-w-md z-50">
          <Dialog.Title className="text-xl font-bold mb-4">
            Réinitialiser le mot de passe
          </Dialog.Title>
          <p className="mb-6">
            Êtes-vous sûr de vouloir réinitialiser le mot de passe de cet employé ? Un nouveau mot de passe temporaire sera généré.
          </p>
          <div className="flex justify-end gap-3">
            <Button
              variant="outline"
              onClick={onClose}
              className="flex items-center gap-2"
            >
              <X size={16} />
              Annuler
            </Button>
            <Button
              onClick={onConfirm}
              className="flex items-center gap-2 bg-orange-600 hover:bg-orange-700 text-white"
            >
              <Key size={16} />
              Réinitialiser
            </Button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );



  // Composant pour le formulaire de demande d'accès/réinitialisation
  const DemandeAccesModal = ({
    isOpen,
    onClose,
    employee,
    typeDemande,
    onSubmit,
    onShowToast
  }) => {
    const [raison, setRaison] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [demandeur, setDemandeur] = useState({
      nom: '',
      prenom: '',
      email: '',
      role: ''
    });

    // Charger les infos de l'utilisateur connecté au montage
    useEffect(() => {
      if (isOpen) {
        const userFromStorage = localStorage.getItem('user');
        if (userFromStorage) {
          const userData = JSON.parse(userFromStorage);
          setDemandeur({
            nom: userData.nom || '',
            prenom: userData.prenom || '',
            email: userData.email || '',
            role: userData.role || userData.role_nom || 'Utilisateur'
          });
        }
      }
    }, [isOpen]);

    const handleSubmit = async (e) => {
      e.preventDefault();

      if (!raison.trim()) {
        if (onShowToast) {
          onShowToast({
            message: 'Veuillez indiquer la raison de la demande',
            type: 'error'
          });
        }
        return;
      }

      setIsSubmitting(true);

      // 🔄 Toast de loading
      if (onShowToast) {
        onShowToast({
          message: 'Envoi de la demande en cours...',
          type: 'loading'
        });
      }

      try {
        // Le backend récupérera automatiquement le demandeur via req.employe
        const demandeData = {
          employeeId: employee.id,
          typeDemande: typeDemande,
          raison: raison.trim()
        };

        await onSubmit(demandeData);

        // ✅ Toast de succès
        if (onShowToast) {
          onShowToast({
            message: 'Demande envoyée avec succès',
            type: 'success'
          });
        }

        setRaison('');
        onClose();

      } catch (error) {
        console.error('❌ Erreur lors de l\'envoi de la demande:', error);

        // ❌ Toast d'erreur
        if (onShowToast) {
          onShowToast({
            message: 'Erreur lors de l\'envoi de la demande',
            type: 'error'
          });
        }
      } finally {
        setIsSubmitting(false);
      }
    };

    return (
      <Dialog.Root open={isOpen} onOpenChange={() => { }}>
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 bg-black/50 z-50" />
          <Dialog.Content className="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-white rounded-xl p-6 max-w-lg w-full mx-4 z-50">
            <Dialog.Title className="text-xl font-bold mb-4">
              {typeDemande === 'creation'
                ? 'Demande de création d\'accès'
                : 'Demande de réinitialisation de mot de passe'
              }
            </Dialog.Title>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Employé concerné
                </label>
                <div className="bg-gray-50 p-3 rounded-lg border border-gray-200 text-gray-700 font-medium">
                  {employee.prenom} {employee.nom}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Raison de la demande *
                </label>
                <Textarea
                  value={raison}
                  onChange={(e) => setRaison(e.target.value)}
                  placeholder="Indiquez la raison de cette demande..."
                  rows={4}
                  className="w-full"
                  required
                  disabled={isSubmitting}
                />
              </div>

              {/* Informations du demandeur (lecture seule) */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h4 className="font-medium text-blue-800 mb-3 flex items-center gap-2">
                  <User size={16} />
                  Informations du demandeur (vous)
                </h4>

                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <label className="block text-blue-700 font-medium mb-1">Nom</label>
                    <div className="bg-white p-2 rounded border text-gray-800">
                      {demandeur.nom || 'Non renseigné'}
                    </div>
                  </div>
                  <div>
                    <label className="block text-blue-700 font-medium mb-1">Prénom</label>
                    <div className="bg-white p-2 rounded border text-gray-800">
                      {demandeur.prenom || 'Non renseigné'}
                    </div>
                  </div>
                </div>

                <div className="mt-3">
                  <label className="block text-blue-700 font-medium mb-1">Rôle</label>
                  <div className="bg-white p-2 rounded border text-gray-800">
                    {demandeur.role || 'Utilisateur'}
                  </div>
                </div>

                <div className="mt-3">
                  <label className="block text-blue-700 font-medium mb-1">Email</label>
                  <div className="bg-white p-2 rounded border text-gray-800 text-xs">
                    {demandeur.email || 'Non renseigné'}
                  </div>
                </div>

                <p className="text-xs text-blue-600 mt-3">
                  ℹ️ Ces informations sont récupérées automatiquement et seront jointes à votre demande
                </p>
              </div>

              <div className="flex justify-end gap-3 mt-6">
                <Button
                  type="button"
                  variant="outline"
                  onClick={onClose}
                  disabled={isSubmitting}
                  className="px-5 py-2.5 text-sm"
                >
                  Annuler
                </Button>
                <Button
                  type="submit"
                  className="bg-[#3084b5] hover:bg-[#3084b5]/90 text-white px-5 py-2.5 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <span className="flex items-center gap-2">
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      Envoi...
                    </span>
                  ) : (
                    'Envoyer la demande'
                  )}
                </Button>
              </div>
            </form>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    );
  };





  const [accessData, setAccessData] = useState({
    demandePendante: null,
    motDePasseTemporaire: null,
    historiqueConnexions: []
  });
  // Suspension d'acces. Distincte du statut d'emploi : `statut` decrit l'emploi
  // et sert aux affectations, a la paie et aux listings ; fermer un acces en y
  // passant quelqu'un a « inactif » le sortirait de ses classes et de sa paie.
  const [panneauSuspension, setPanneauSuspension] = useState(false);
  const [motifSuspension, setMotifSuspension] = useState('');
  const [suspensionEnCours, setSuspensionEnCours] = useState(false);

  const basculerAcces = async (suspendre) => {
    setSuspensionEnCours(true);
    try {
      const r = await api.employes.setAccesSuspendu(
        localEmployee.id, suspendre, suspendre ? motifSuspension.trim() : null
      );
      if (r?.success) {
        setLocalEmployee((precedent) => ({
          ...precedent,
          acces_suspendu: suspendre ? 1 : 0,
          acces_suspendu_motif: r.data?.acces_suspendu_motif || null,
          acces_suspendu_par: r.data?.acces_suspendu_par || null
        }));
        setPanneauSuspension(false);
        setMotifSuspension('');
        showSuccessToast(r.message);
      } else if (onShowToast) {
        onShowToast({ message: r?.message || "Changement d'accès impossible", type: 'error' });
      }
    } catch (erreur) {
      if (onShowToast) onShowToast({ message: erreur.message, type: 'error' });
    } finally {
      setSuspensionEnCours(false);
    }
  };

  const [isDemandeModalOpen, setIsDemandeModalOpen] = useState(false);
  const [typeDemande, setTypeDemande] = useState(null);
  const [profileChangeHistory, setProfileChangeHistory] = useState([]);
  const [isLoadingProfileChangeHistory, setIsLoadingProfileChangeHistory] = useState(false);

  // Ajouter ces effets pour charger les données d'accès
  useEffect(() => {
    if (employee.id && isOpen) {
      loadAccessData();
      loadConnectionHistory();
      loadProfileChangeHistory();
    }
  }, [employee.id, isOpen]);


  useEffect(() => {
    if (isOpen) {
      setActiveTab('details'); // Toujours revenir à l'onglet details à l'ouverture
    }
  }, [isOpen]);





  const loadAccessData = async () => {
    try {
      console.log('Chargement des données d\'accès pour employé:', employee.id);

      // Charger seulement les données d'accès (sans la page pour l'historique)
      const accessResponse = await api.employes.getAccessData(employee.id);
      console.log('Response complète:', accessResponse);
      console.log('demandeAcceptee dans la réponse:', accessResponse.demandeAcceptee);

      setAccessData(accessResponse);

      // Si une demande a été acceptée, l'ajouter aux données de l'employé
      if (accessResponse.demandeAcceptee) {
        console.log('Demande acceptée trouvée:', accessResponse.demandeAcceptee);
        setLocalEmployee(prev => ({
          ...prev,
          demandeAcceptee: accessResponse.demandeAcceptee
        }));
        console.log('localEmployee mis à jour avec demandeAcceptee');
      } else {
        console.log('Aucune demande acceptée trouvée');
      }
    } catch (error) {
      console.error('Erreur lors du chargement des données d\'accès:', error);
    }
  };

  const loadProfileChangeHistory = async () => {
    try {
      setIsLoadingProfileChangeHistory(true);
      const response = await api.employes.getProfileChangeHistory(employee.id, 50);
      if (response?.success) {
        setProfileChangeHistory(Array.isArray(response.data) ? response.data : []);
      } else {
        setProfileChangeHistory([]);
      }
    } catch (error) {
      console.error('Erreur lors du chargement de l historique RH:', error);
      setProfileChangeHistory([]);
    } finally {
      setIsLoadingProfileChangeHistory(false);
    }
  };

  const loadConnectionHistory = async (page = 1) => {
    try {
      setIsLoadingConnectionHistory(true);

      // Charger seulement l'historique avec la page spécifiée
      const accessResponse = await api.employes.getAccessData(employee.id, page);

      // Mettre à jour seulement l'historique et sa pagination
      setAccessData(prev => ({
        ...prev,
        historiqueConnexions: accessResponse.historiqueConnexions,
        connectionHistoryPagination: accessResponse.connectionHistoryPagination
      }));

      // Mettre à jour la pagination
      if (accessResponse.connectionHistoryPagination) {
        setConnectionHistoryPagination(accessResponse.connectionHistoryPagination);
      }
    } catch (error) {
      console.error('Erreur lors du chargement de l\'historique de connexions:', error);
    } finally {
      setIsLoadingConnectionHistory(false);
    }
  };


  // Fonction pour traiter une nouvelle demande
  const handleSubmitDemande = async (demandeData) => {
    try {
      console.log('📋 Données de la demande:', demandeData);

      // ✅ Structure des données incluant l'ID du demandeur
      const requestData = {
        employeeId: demandeData.employeeId,
        typeDemande: demandeData.typeDemande,
        raison: demandeData.raison,
        // ✅ NOUVEAU : Inclure l'ID du demandeur (utilisateur connecté)
        demandeurId: demandeData.demandeurId,
        demandeurNom: demandeData.demandeurNom,
        demandeurPrenom: demandeData.demandeurPrenom,
        demandeurEmail: demandeData.demandeurEmail,
        demandeurRole: demandeData.demandeurRole
      };

      console.log('📤 Envoi de la demande avec structure:', requestData);

      const response = await api.employes.createAccessRequest(requestData);
      if (response.success) {
        showSuccessToast('Demande soumise avec succès');
        await loadAccessData(); // Recharger les données
      } else {
        throw new Error(response.message || 'Erreur lors de la soumission');
      }
    } catch (error) {
      console.error('Erreur lors de la soumission de la demande:', error);
      throw error;
    }
  };

  // Fonction pour révéler/masquer le mot de passe temporaire
  const [showTemporaryPassword, setShowTemporaryPassword] = useState(false);

  const getProfileHistoryChanges = (entry = {}) => {
    const changes = entry.champs_modifies || entry.payload_demande?.champs || {};
    return changes && typeof changes === 'object' ? Object.entries(changes) : [];
  };

  const getProfileHistoryStatus = (status) => {
    if (status === 'approuvee') {
      return { label: 'Approuvée', className: 'bg-emerald-50 text-emerald-700 border-emerald-200' };
    }
    if (status === 'refusee') {
      return { label: 'Refusée', className: 'bg-red-50 text-red-700 border-red-200' };
    }
    return { label: 'En attente', className: 'bg-amber-50 text-amber-700 border-amber-200' };
  };

  const formatHistoryPerson = (prenom, nom) => {
    return [prenom, nom].filter(Boolean).join(' ').trim() || 'Non renseigné';
  };
  return (
    <Dialog.Root open={isOpen} onOpenChange={() => { }}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/50 z-40" />

        <Dialog.Content className="fixed top-[55%] left-[50%] transform -translate-x-1/2 -translate-y-1/2 bg-white rounded-xl shadow-2xl w-full max-w-6xl max-h-[85vh] overflow-y-auto z-50">

          <Tabs.Root value={activeTab} onValueChange={setActiveTab}>
            {/* En-tête fixe */}
            <div className="sticky top-0 bg-white z-10">
              <div className="p-5 border-b border-gray-200 flex justify-between items-center">
                <Dialog.Title className="text-xl font-bold text-[#3084b5]">
                  {isEditing ? 'Modifier un employé' : "Dossier de l'employé"}
                </Dialog.Title>


                <button
                  onClick={onClose}
                  className="p-2 rounded-full bg-gray-100 hover:bg-gray-200 focus:outline-none transition-colors"
                >
                  <X size={20} className="text-gray-700" />
                </button>

              </div>

              <Tabs.List className="flex flex-wrap bg-gray-50 px-5">
                <Tabs.Trigger
                  value="details"
                  className={`px-3 py-3 font-medium flex items-center gap-2 whitespace-nowrap ${activeTab === 'details'
                      ? 'text-[#3084b5] border-b-2 border-[#3084b5]'
                      : 'text-gray-600 hover:text-gray-800'
                    }`}
                >

                  <IdCard size={18} />
                  Informations personnelles
                </Tabs.Trigger>

                <Tabs.Trigger
                  value="documents"
                  className={`px-3 py-3 font-medium flex items-center gap-2 whitespace-nowrap ${activeTab === 'documents'
                      ? 'text-[#3084b5] border-b-2 border-[#3084b5]'
                      : 'text-gray-600 hover:text-gray-800'
                    }`}
                >

                  <Paperclip size={18} />
                  Documents professionnels
                </Tabs.Trigger>

                {/* Les enfants à charge n'existent que si l'école facture des
                    frais scolaires : sans comptabilité, il n'y a rien à réduire. */}
                {enfantsOuvert && (
                  <Tabs.Trigger
                    value="enfants"
                    className={`px-3 py-3 font-medium flex items-center gap-2 whitespace-nowrap ${activeTab === 'enfants'
                        ? 'text-[#3084b5] border-b-2 border-[#3084b5]'
                        : 'text-gray-600 hover:text-gray-800'
                      }`}
                  >
                    <Users size={18} />
                    Enfants à charge
                  </Tabs.Trigger>
                )}


                <Tabs.Trigger
                  value="access"
                  className={`px-3 py-3 font-medium flex items-center gap-2 whitespace-nowrap ${activeTab === 'access'
                      ? 'text-[#3084b5] border-b-2 border-[#3084b5]'
                      : 'text-gray-600 hover:text-gray-800'
                    }`}
                >

                  <Lock size={18} />
                  Accès et sécurité
                </Tabs.Trigger>

                {/* L'espace enseignant est une autre porte que la connexion à
                    l'administration : son onglet se range juste après
                    « Accès et sécurité », pour qu'on ne cherche pas l'une
                    dans l'autre. */}
                {espaceEnseignantOuvert && (
                  <Tabs.Trigger
                    value="acces_enseignant"
                    className={`px-3 py-3 font-medium flex items-center gap-2 whitespace-nowrap ${activeTab === 'acces_enseignant'
                        ? 'text-[#3084b5] border-b-2 border-[#3084b5]'
                        : 'text-gray-600 hover:text-gray-800'
                      }`}
                  >
                    <KeyRound size={18} />
                    Accès enseignant
                  </Tabs.Trigger>
                )}

                <Tabs.Trigger
                  value="profile_history"
                  className={`px-3 py-3 font-medium flex items-center gap-2 whitespace-nowrap ${activeTab === 'profile_history'
                      ? 'text-[#3084b5] border-b-2 border-[#3084b5]'
                      : 'text-gray-600 hover:text-gray-800'
                    }`}
                >
                  <History size={18} />
                  Historique RH
                </Tabs.Trigger>
              </Tabs.List>
            </div>

            {/* Message d'erreur si présent */}
            {error && (
              <div className="m-5 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 flex items-start gap-3">
                <div className="text-red-500 mt-0.5">
                  <AlertTriangle size={20} />
                </div>
                <div>
                  {error.split('\n').map((line, i) => (
                    <p key={i}>{line}</p>
                  ))}
                </div>
              </div>
            )}

            {/* Corps avec Tabs - le défilement est géré par le Dialog.Content */}
            <Tabs.Content value="details">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 p-5 pb-10">
                {/* Colonne de gauche - Photo et informations de base */}
                <div className="space-y-6">
                  {/* Photo de l'employé */}
                  <div className="bg-white rounded-xl overflow-hidden shadow-sm border border-gray-200">
                    <div className="p-4 bg-gray-50 border-b border-gray-200">
                      <h3 className="font-semibold text-gray-700">Photo d'identité</h3>
                    </div>

                    <div className="p-5 flex flex-col items-center">
                      <div className="relative w-40 h-40 bg-gray-100 rounded-xl overflow-hidden border-2 border-gray-200 mb-4">
                        {cameraActive ? (
                          <video ref={videoRef} autoPlay playsInline muted className="w-full h-full object-cover bg-gray-900" />
                        ) : photoPreview ? (
                          <img
                            src={photoPreview}
                            alt={`${localEmployee.prenom} ${localEmployee.nom}`}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full flex flex-col items-center justify-center text-gray-400">
                            <div className="w-32 h-32 bg-[#246990] rounded-full flex items-center justify-center text-white text-5xl">
                              {localEmployee.nom && localEmployee.nom[0]}{localEmployee.prenom && localEmployee.prenom[0]}
                            </div>
                          </div>
                        )}

                        {isUploading && (
                          <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
                            <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-white"></div>
                          </div>
                        )}
                      </div>

                      {/* Boutons gestion photo */}
                      <div className="flex flex-wrap gap-2 w-full">
                        {isEditing && cameraActive ? (
                          <>
                            <Button
                              size="sm"
                              onClick={handleCapturePhoto}
                              className="flex items-center justify-center gap-2 flex-1 border border-transparent bg-[#246990] text-white hover:bg-[#1f5a7a]"
                            >
                              <Camera size={16} />
                              Capturer
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={stopCamera}
                              className="flex items-center justify-center gap-2 flex-1"
                            >
                              <X size={16} />
                              Annuler
                            </Button>
                          </>
                        ) : isEditing ? (
                          <>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={handleSelectPhoto}
                              disabled={isUploading}
                              className="flex items-center justify-center gap-2 flex-1"
                            >
                              <Upload size={16} />
                              Importer
                            </Button>

                            <Button
                              variant="outline"
                              size="sm"
                              onClick={startCamera}
                              disabled={isUploading}
                              className="flex items-center justify-center gap-2 flex-1 text-[#246990]"
                            >
                              <Camera size={16} />
                              Prendre
                            </Button>

                            {photoPreview && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={handleDeletePhoto}
                                className="flex items-center gap-2 text-red-600 hover:bg-red-50"
                              >
                                <Trash2 size={16} />
                              </Button>
                            )}
                          </>
                        ) : (
                          <div className="text-sm text-gray-500 text-center w-full">
                            {photoPreview ? 'Photo disponible' : 'Aucune photo disponible'}
                          </div>
                        )}

                        <input
                          type="file"
                          ref={fileInputRef}
                          className="hidden"
                          accept="image/jpeg,image/png,image/jpg"
                          onChange={handleFileChange}
                        />
                      </div>

                      {cameraError && (
                        <div className="mt-3 w-full rounded-lg border border-red-100 bg-red-50 px-3 py-2 text-xs text-red-600">
                          {cameraError}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Informations professionnelles */}
                  <div className="bg-white rounded-xl overflow-hidden shadow-sm border border-gray-200">
                    <div className="p-4 bg-gray-50 border-b border-gray-200">
                      <h3 className="font-semibold text-gray-700">Informations professionnelles</h3>
                    </div>


                    <div className="p-5 space-y-4">
                      {/* <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Département</label>
                        {isEditing ? (
                          <select
                            name="departement_id"
                            value={localEmployee.departement_id || ''}
                            onChange={handleInputChange}
                            className="w-full px-4 py-2.5 mt-1 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#246990] focus:border-transparent"
                          >
                            <option value="">Sélectionner un département</option>
                            {departements.map(dept => (
                              <option key={dept.id} value={dept.id}>{dept.nom}</option>
                            ))}
                          </select>
                        ) : (
                          <div className="font-medium bg-gray-50 p-2 rounded-lg border border-gray-200">
                            {localEmployee.departement_nom || 'Non renseigné'}
                          </div>
                        )}
                      </div>*/}

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Date d'embauche</label>
                        {isEditing ? (
                          <Input
                            type="date"
                            name="date_embauche"
                            value={localEmployee.date_embauche || ''}
                            onChange={handleInputChange}
                            className="w-full"
                          />
                        ) : (
                          <div className="font-medium bg-gray-50 p-2 rounded-lg border border-gray-200">
                            {formatDate(localEmployee.date_embauche)}
                          </div>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Type de contrat</label>
                        {isEditing ? (
                          <select
                            name="info_pro_type_contrat"
                            value={localEmployee.info_pro_type_contrat || ''}
                            onChange={handleInputChange}
                            className="w-full px-4 py-2.5 mt-1 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#246990] focus:border-transparent"
                          >
                            <option value="">Sélectionner un type</option>
                            <option value="CDI">CDI</option>
                            <option value="CDD">CDD</option>
                            <option value="STAGE">Stage</option>
                            <option value="ALTERNANCE">Alternance</option>
                            <option value="AUTRE">Autre</option>
                          </select>
                        ) : (
                          <div className="font-medium bg-gray-50 p-2 rounded-lg border border-gray-200">
                            {localEmployee.info_pro_type_contrat || 'Non renseigné'}
                          </div>
                        )}
                      </div>

                      {pointageOuvert && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Numéro de badge</label>
                        {isEditing ? (
                          <Input
                            type="text"
                            name="numero_badge"
                            value={localEmployee.numero_badge || ''}
                            onChange={handleInputChange}
                            placeholder="Numéro de badge"
                            className="w-full"
                          />
                        ) : (
                          <div className="font-medium bg-gray-50 p-2 rounded-lg border border-gray-200">
                            {localEmployee.numero_badge || 'Non renseigné'}
                          </div>
                        )}
                      </div>
                      )}

                      {/* Badge RFID.
                          Il ne pouvait être associé qu'à la création : un employé sans badge
                          devait être recréé pour en recevoir un. Le lecteur se comporte comme
                          un clavier, d'où le champ de capture plutôt qu'une saisie libre. */}
                      {rfidOuvert && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Badge RFID</label>
                        {isEditing ? (
                          <ScanBadgeRfid
                            valeur={localEmployee.rfid_uid || ''}
                            onChange={(uid) => setLocalEmployee((prev) => ({ ...prev, rfid_uid: uid }))}
                            onScan={(uid) => showSuccessToast(`Badge lu : ${uid}. Enregistrez pour le lier.`)}
                          />
                        ) : (
                          <div className="font-medium bg-gray-50 p-2 rounded-lg border border-gray-200">
                            {localEmployee.rfid_uid || 'Aucun badge lié'}
                          </div>
                        )}

                        {/* QUI A ATTRIBUÉ CE BADGE, ET QUAND.
                            Écrit sous le champ, en consultation seulement : en
                            modification, le champ du badge est en train de
                            changer et la mention parlerait du passé juste
                            au-dessus de la main qui le remplace.

                            Elle ne s'affiche QUE si la date existe. Les badges
                            posés avant le 4 octobre 2026 n'ont laissé aucune
                            trace, et une ligne « attribué par inconnu » sur la
                            moitié des fiches serait du bruit : mieux vaut ne
                            rien dire que dire qu'on ne sait pas.

                            ⚠️ La date arrive DÉJÀ ÉCRITE par le serveur, sous
                            `badge_attribue_le_fr`, et non dans la colonne brute
                            que porte `e.*` : celle-là revient en objet Date, que
                            le pilote rend en UTC, et un badge attribué à 00 h 30
                            s'afficherait la veille. Ne jamais la reconstruire
                            ici avec `new Date(...)`. */}
                        {!isEditing && localEmployee.badge_attribue_le_fr && (
                          <p className="mt-1.5 text-xs text-gray-500">
                            Attribué le{' '}
                            <span className="font-medium text-gray-600">
                              {localEmployee.badge_attribue_le_fr}
                            </span>
                            {localEmployee.badge_attribue_par_nom
                              ? <> par <span className="font-medium text-gray-600">{localEmployee.badge_attribue_par_nom}</span></>
                              : null}
                          </p>
                        )}
                      </div>
                      )}


                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Statut</label>
                        {isEditing ? (
                          <select
                            name="statut"
                            value={localEmployee.statut || 'actif'}
                            onChange={handleInputChange}
                            className="w-full px-4 py-2.5 mt-1 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#246990] focus:border-transparent"
                          >
                            <option value="actif">Actif</option>
                            <option value="conge">En congé</option>
                            <option value="suspendu">Suspendu</option>
                          </select>
                        ) : (
                          <div className="font-medium bg-gray-50 p-2 rounded-lg border border-gray-200">
                            {localEmployee.statut === 'actif' ? 'Actif' :
                              localEmployee.statut === 'conge' ? 'En congé' :
                                localEmployee.statut === 'suspendu' ? 'Suspendu' :
                                  localEmployee.statut || 'Actif'}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>



                  {/* Informations de création */}
                  <div className="bg-white rounded-xl overflow-hidden shadow-sm border border-gray-200">
                    <div className="p-4 bg-gray-50 border-b border-gray-200">
                      <h3 className="font-semibold text-gray-700">Informations de création</h3>
                    </div>

                    <div className="p-5 space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Employé créé par</label>
                        <div className="font-medium bg-gray-50 p-2 rounded-lg border border-gray-200">
                          {localEmployee.created_by_nom || 'Admin'}
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Date et heure de création</label>
                        <div className="font-medium bg-gray-50 p-2 rounded-lg border border-gray-200">
                          {formatCreationDateTime(localEmployee.created_at)}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Colonne du milieu - Informations de l'employé */}
                <div className="space-y-6">
                  <div className="bg-white rounded-xl overflow-hidden shadow-sm border border-gray-200">
                    <div className="p-4 bg-gray-50 border-b border-gray-200">
                      <h3 className="font-semibold text-gray-700">Identité</h3>
                    </div>

                    <div className="p-5 space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Nom</label>
                        {isEditing ? (
                          <Input
                            name="nom"
                            value={localEmployee.nom || ''}
                            onChange={handleInputChange}
                            className="w-full"
                          />
                        ) : (
                          <div className="font-medium bg-gray-50 p-2 rounded-lg border border-gray-200">
                            {localEmployee.nom || 'Non renseigné'}
                          </div>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Prénom</label>
                        {isEditing ? (
                          <Input
                            name="prenom"
                            value={localEmployee.prenom || ''}
                            onChange={handleInputChange}
                            className="w-full"
                          />
                        ) : (
                          <div className="font-medium bg-gray-50 p-2 rounded-lg border border-gray-200">
                            {localEmployee.prenom || 'Non renseigné'}
                          </div>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Date de naissance</label>
                        {isEditing ? (
                          <Input
                            type="date"
                            name="date_naissance"
                            value={localEmployee.date_naissance || ''}
                            onChange={handleInputChange}
                            className="w-full"
                          />
                        ) : (
                          <div className="font-medium bg-gray-50 p-2 rounded-lg border border-gray-200">
                            {formatDate(localEmployee.date_naissance)}
                          </div>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Sexe</label>
                        {isEditing ? (
                          <select
                            name="sexe"
                            value={localEmployee.sexe || 'M'}
                            onChange={handleInputChange}
                            className="w-full px-4 py-2.5 mt-1 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#246990] focus:border-transparent"
                          >
                            <option value="M">Masculin</option>
                            <option value="F">Féminin</option>
                          </select>
                        ) : (
                          <div className="font-medium bg-gray-50 p-2 rounded-lg border border-gray-200">
                            {localEmployee.sexe === 'M' ? 'Masculin' : 'Féminin'}
                          </div>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                        {isEditing ? (
                          <Input
                            type="email"
                            name="email"
                            value={localEmployee.email || ''}
                            onChange={handleInputChange}
                            className="w-full"
                          />
                        ) : (
                          <div className="font-medium bg-gray-50 p-2 rounded-lg border border-gray-200">
                            {localEmployee.email || 'Non renseigné'}
                          </div>
                        )}
                      </div>

                      <div>
                        <div className="flex items-center justify-between gap-3 mb-1">
                          <label className="block text-sm font-medium text-gray-700">Téléphone</label>
                          {isEditing && telephonesSecondaires.length < MAX_TELEPHONES_SECONDAIRES && (
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={ajouterTelephoneSecondaire}
                              className="gap-2 text-[#246990]"
                            >
                              <Plus className="w-4 h-4" />
                              Ajouter un numéro
                            </Button>
                          )}
                        </div>
                        {isEditing ? (
                          <>
                            <Input
                              type="tel"
                              name="telephone"
                              value={localEmployee.telephone || ''}
                              onChange={handleInputChange}
                              placeholder="Numéro principal"
                              className={`w-full ${telephoneMalForme(localEmployee.telephone) ? '!border-red-300' : ''}`}
                            />
                            {telephoneMalForme(localEmployee.telephone) && (
                              <p className="mt-1 text-xs text-red-600">{MESSAGE_TELEPHONE}</p>
                            )}
                          </>
                        ) : (
                          <div className="font-medium bg-gray-50 p-2 rounded-lg border border-gray-200">
                            {localEmployee.telephone || 'Non renseigné'}
                          </div>
                        )}
                      </div>

                      {/* Les numéros secondaires, un champ chacun : deux numéros
                          dans un seul champ faisaient refuser la fiche. */}
                      {telephonesSecondaires.length > 0 && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Autres numéros</label>
                          {isEditing ? (
                            <div className="space-y-2">
                              {telephonesSecondaires.map((numero, index) => (
                                <div key={index}>
                                  <div className="flex items-center gap-2">
                                    <Input
                                      type="tel"
                                      value={numero}
                                      onChange={(e) => modifierTelephoneSecondaire(index, e.target.value)}
                                      placeholder={`Numéro secondaire ${index + 1}`}
                                      className={`w-full ${telephoneMalForme(numero) ? '!border-red-300' : ''}`}
                                    />
                                    <Button
                                      type="button"
                                      variant="outline"
                                      size="icon"
                                      onClick={() => retirerTelephoneSecondaire(index)}
                                      title="Retirer ce numéro"
                                      aria-label={`Retirer le numéro secondaire ${index + 1}`}
                                      className="flex-shrink-0 text-gray-500 hover:text-red-600"
                                    >
                                      <X className="w-4 h-4" />
                                    </Button>
                                  </div>
                                  {telephoneMalForme(numero) && (
                                    <p className="mt-1 text-xs text-red-600">{MESSAGE_TELEPHONE}</p>
                                  )}
                                </div>
                              ))}
                            </div>
                          ) : (
                            <div className="font-medium bg-gray-50 p-2 rounded-lg border border-gray-200 space-y-0.5">
                              {telephonesSecondaires.map((numero, index) => (
                                <div key={`${numero}-${index}`}>{numero}</div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Adresse */}
                  <div className="bg-white rounded-xl overflow-hidden shadow-sm border border-gray-200">
                    <div className="p-4 bg-gray-50 border-b border-gray-200">
                      <h3 className="font-semibold text-gray-700">Adresse</h3>
                    </div>

                    <div className="p-5 space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Adresse complète</label>
                        {isEditing ? (
                          <Textarea
                            name="adresse"
                            value={localEmployee.adresse || ''}
                            onChange={handleInputChange}
                            className="w-full"
                            rows={5}
                          />
                        ) : (
                          <div className="font-medium bg-gray-50 p-2 rounded-lg border border-gray-200 min-h-[5rem]">
                            {localEmployee.adresse || 'Non renseigné'}
                          </div>
                        )}
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Ville</label>
                          {isEditing ? (
                            <Input
                              name="ville"
                              value={localEmployee.ville || ''}
                              onChange={handleInputChange}
                              className="w-full"
                            />
                          ) : (
                            <div className="font-medium bg-gray-50 p-2 rounded-lg border border-gray-200">
                              {localEmployee.ville || 'Non renseigné'}
                            </div>
                          )}
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Code postal</label>
                          {isEditing ? (
                            <Input
                              name="code_postal"
                              value={localEmployee.code_postal || ''}
                              onChange={handleInputChange}
                              className="w-full"
                            />
                          ) : (
                            <div className="font-medium bg-gray-50 p-2 rounded-lg border border-gray-200">
                              {localEmployee.code_postal || 'Non renseigné'}
                            </div>
                          )}
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Pays</label>
                        {isEditing ? (
                          <Input
                            name="pays"
                            value={localEmployee.pays || ''}
                            onChange={handleInputChange}
                            className="w-full"
                          />
                        ) : (
                          <div className="font-medium bg-gray-50 p-2 rounded-lg border border-gray-200">
                            {localEmployee.pays || 'Non renseigné'}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Colonne de droite - Autres informations */}
                <div className="space-y-6">
                  {/* Contact d'urgence */}
                  <div className="bg-white rounded-xl overflow-hidden shadow-sm border border-gray-200">
                    <div className="p-4 bg-gray-50 border-b border-gray-200">
                      <h3 className="font-semibold text-gray-700 flex items-center gap-2">
                        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-red-500">
                          <path d="M8 2h8a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2Z"></path>
                          <path d="M8 6h4"></path>
                          <path d="M8 10h4"></path>
                          <path d="M8 14h4"></path>
                          <path d="m17 22-5-4-5 4V4h10v18Z"></path>
                        </svg>
                        Contact d'urgence
                      </h3>
                    </div>

                    <div className="p-5 space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Nom et prénom</label>
                        {isEditing ? (
                          <Input
                            name="contact_urgence.nom_prenom"
                            value={contactUrgence?.nom_prenom || ''}
                            onChange={handleInputChange}
                            className="w-full"
                            placeholder="Nom et prénom du contact d'urgence"
                          />
                        ) : (
                          <div className="font-medium bg-gray-50 p-2 rounded-lg border border-gray-200">
                            {contactUrgence?.nom_prenom || 'Non renseigné'}
                          </div>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Relation</label>
                        {isEditing ? (
                          <Input
                            name="contact_urgence.relation"
                            value={contactUrgence?.relation || ''}
                            onChange={handleInputChange}
                            className="w-full"
                            placeholder="Ex: Conjoint(e), parent, ami(e)..."
                          />
                        ) : (
                          <div className="font-medium bg-gray-50 p-2 rounded-lg border border-gray-200">
                            {contactUrgence?.relation || 'Non renseigné'}
                          </div>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Téléphone</label>
                        {isEditing ? (
                          <Input
                            type="tel"
                            name="contact_urgence.telephone"
                            value={contactUrgence?.telephone || ''}
                            onChange={handleInputChange}
                            className="w-full"
                            placeholder="Numéro de téléphone d'urgence"
                          />
                        ) : (
                          <div className="font-medium bg-gray-50 p-2 rounded-lg border border-gray-200">
                            {contactUrgence?.telephone || 'Non renseigné'}
                          </div>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                        {isEditing ? (
                          <Input
                            type="email"
                            name="contact_urgence.email"
                            value={contactUrgence?.email || ''}
                            onChange={handleInputChange}
                            className="w-full"
                            placeholder="Email du contact d'urgence"
                          />
                        ) : (
                          <div className="font-medium bg-gray-50 p-2 rounded-lg border border-gray-200">
                            {contactUrgence?.email || 'Non renseigné'}
                          </div>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Adresse</label>
                        {isEditing ? (
                          <Textarea
                            name="contact_urgence.adresse"
                            value={contactUrgence?.adresse || ''}
                            onChange={handleInputChange}
                            className="w-full"
                            rows={2}
                            placeholder="Adresse du contact d'urgence"
                          />
                        ) : (
                          <div className="font-medium bg-gray-50 p-2 rounded-lg border border-gray-200 min-h-[3rem]">
                            {contactUrgence?.adresse || 'Non renseigné'}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>



                  {/* Accès à l'application (version simplifiée - infos complètes dans l'onglet Accès) */}
                  <div className="bg-white rounded-xl overflow-hidden shadow-sm border border-gray-200">
                    <div className="p-4 bg-gray-50 border-b border-gray-200">
                      <h3 className="font-semibold text-gray-700">Accès à l'application</h3>
                    </div>

                    <div className="p-5 space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Statut d'accès</label>
                        <div className={`flex items-center gap-2 font-medium p-2 rounded-lg ${(localEmployee.info_pro_acces_application === 1 || localEmployee.acces_application) ? 'bg-green-50 text-green-700' : 'bg-gray-50 text-gray-700'}`}>
                          {(localEmployee.info_pro_acces_application === 1 || localEmployee.acces_application) ? (
                            <>
                              <Unlock size={18} className="text-green-600" />
                              <span>Accès autorisé</span>
                            </>
                          ) : (
                            <>
                              <Lock size={18} className="text-gray-600" />
                              <span>Pas d'accès</span>
                            </>
                          )}
                        </div>

                        {/* Ouvrir un accès exige des identifiants : cela se fait dans l'onglet
                            « Accès et sécurité ». Ce drapeau n'en est que le reflet — la
                            connexion ne vérifie que l'identifiant et le statut de l'employé,
                            jamais lui. Modifiable ici, il laissait annoncer un accès qui
                            n'existait pas, et le décocher ne révoquait rien. */}
                        {isEditing && (
                          <p className="mt-2 flex items-start gap-1.5 text-[11px] leading-snug text-gray-500">
                            <Info size={13} className="mt-px flex-shrink-0 text-gray-400" />
                            L'ouverture et la révocation d'un accès se font dans l'onglet « Accès et sécurité ».
                          </p>
                        )}
                      </div>

                      {(localEmployee.info_pro_acces_application === 1 || localEmployee.acces_application) && (
                        <div className="mt-3">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setActiveTab('access')}
                            className="w-full flex items-center justify-center gap-2"
                          >
                            <Settings size={16} />
                            Gérer les accès
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </Tabs.Content>
            <Tabs.Content value="documents">
              <div className="p-5 space-y-6 pb-10">


                {/* Section Contrat */}
                <div className="bg-gradient-to-r from-indigo-50 to-blue-50 rounded-xl overflow-hidden shadow-sm border border-blue-200">
                  <div className="p-4 bg-[#246990] border-b border-blue-700">
                    <h3 className="font-semibold text-white flex items-center gap-2">
                      <FileText size={18} />
                      Contrat de travail
                    </h3>
                  </div>

                  <div className="p-5">
                    <div className="flex md:flex-row flex-col items-start gap-6">
                      {/* Zone de génération de contrat */}
                      <div className="w-full md:w-1/2">
                        <h4 className="font-medium text-gray-700 mb-3">Générer un nouveau contrat</h4>

                        {/* Vérifications des informations requises */}
                        <div className="mb-4">
                          <p className="text-sm text-gray-600 mb-2">Informations requises :</p>
                          <div className="space-y-1 text-sm">
                            <div className="flex items-center gap-2">
                              {localEmployee.nom && localEmployee.prenom ?
                                <span className="text-green-600">✓</span> :
                                <span className="text-red-600">✗</span>
                              }
                              <span className={localEmployee.nom && localEmployee.prenom ? 'text-green-700' : 'text-red-700'}>
                                Nom et prénom
                              </span>
                            </div>
                            <div className="flex items-center gap-2">
                              {localEmployee.date_embauche ?
                                <span className="text-green-600">✓</span> :
                                <span className="text-red-600">✗</span>
                              }
                              <span className={localEmployee.date_embauche ? 'text-green-700' : 'text-red-700'}>
                                Date d'embauche
                              </span>
                            </div>
                            <div className="flex items-center gap-2">
                              {localEmployee.info_pro_type_contrat ?
                                <span className="text-green-600">✓</span> :
                                <span className="text-orange-600">!</span>
                              }
                              <span className={localEmployee.info_pro_type_contrat ? 'text-green-700' : 'text-orange-700'}>
                                Type de contrat {!localEmployee.info_pro_type_contrat && '(CDI par défaut)'}
                              </span>
                            </div>
                          </div>
                        </div>

                        <Button
                          onClick={generateContract}
                          disabled={isGeneratingContract || hasExistingContract() || !localEmployee.nom || !localEmployee.prenom || !localEmployee.date_embauche}
                          className={`flex items-center gap-2 text-base px-5 py-2 h-auto rounded-lg ${hasExistingContract()
                              ? 'bg-gray-400 cursor-not-allowed'
                              : 'bg-[#319190] hover:bg-[#63b5b4]/90 text-white'
                            }`}
                        >
                          {isGeneratingContract ? (
                            <>
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                              Génération...
                            </>
                          ) : hasExistingContract() ? (
                            <>
                              <FileText size={18} />
                              Contrat déjà généré
                            </>
                          ) : (
                            <>
                              <FileText size={18} />
                              Générer un contrat
                            </>
                          )}
                        </Button>

                        {hasExistingContract() && (
                          <p className="text-sm text-blue-600 mt-2">
                            Un contrat existe déjà. Vous pouvez le modifier si nécessaire.
                          </p>
                        )}
                      </div>

                      {/* Liste des contrats existants */}
                      <div className="w-full md:w-1/2">
                        <h4 className="font-medium text-gray-700 mb-3">Contrats existants</h4>

                        <div className="bg-white border border-gray-200 rounded-lg p-4">
                          {documents.filter(doc => doc.type === 'contrat').length > 0 ? (
                            <div className="space-y-2">
                              {documents.filter(doc => doc.type === 'contrat').map((doc, index) => (
                                <div key={index} className="flex justify-between items-center p-2 hover:bg-gray-50 rounded-lg">
                                  <div className="flex items-center gap-2">
                                    <FileText size={18} className="text-blue-600" />
                                    <div>
                                      <p className="text-sm font-medium">{doc.nom_fichier || `Contrat ${index + 1}`}</p>
                                      <p className="text-xs text-gray-500">
                                        {doc.date_ajout ? new Date(doc.date_ajout).toLocaleDateString() : 'Date inconnue'}
                                      </p>
                                    </div>
                                  </div>
                                  <div className="flex gap-2">
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      onClick={() => handleDownloadDocument(doc.id, doc.nom_fichier)}
                                      className="text-blue-600"
                                    >
                                      <Download size={16} />
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      onClick={() => handleDeleteDocument(doc.id, doc.type)}
                                      className="text-red-600"
                                    >
                                      <Trash2 size={16} />
                                    </Button>
                                  </div>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <div className="text-center py-4 text-gray-500">
                              <FileText size={32} className="mx-auto text-gray-300 mb-2" />
                              Aucun contrat disponible
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>




                {/* Section CV */}
                <div className="bg-white rounded-xl overflow-hidden shadow-sm border border-gray-200">
                  <div className="p-4 bg-gray-50 border-b border-gray-200 flex justify-between items-center">
                    <h3 className="font-semibold text-gray-700 flex items-center gap-2">
                      <FileText size={18} className="text-gray-700" />
                      CV
                    </h3>

                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        const input = document.createElement('input');
                        input.type = 'file';
                        input.accept = 'application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document';
                        input.onchange = (e) => handleFileUpload(e, 'cv');
                        input.click();
                      }}
                      className="flex items-center gap-2"
                    >
                      <Plus size={16} />
                      Ajouter
                    </Button>
                  </div>

                  <div className="p-5">
                    {documents.filter(doc => doc.type === 'cv').length > 0 ? (
                      <div className="space-y-3">
                        {documents.filter(doc => doc.type === 'cv').map((doc, index) => (

                          <div key={index} className="bg-gray-50 p-3 rounded-lg border border-gray-200 flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              {getDocumentIcon(doc)}
                              <div>
                                <p className="font-medium text-gray-800">{doc.nom_fichier || `Document ${index + 1}`}</p>
                                <div className="flex items-center gap-4 text-sm text-gray-500">
                                  <span>{doc.date_ajout ? new Date(doc.date_ajout).toLocaleDateString() : 'Date inconnue'}</span>
                                </div>
                              </div>
                            </div>
                            <div className="flex gap-2">
                              {/* Bouton Télécharger */}
                              <Button
                                variant="ghost"
                                size="sm"
                                className="flex items-center gap-2 text-blue-600 hover:text-blue-900"
                                onClick={() => handleDownloadDocument(doc.id, doc.nom_fichier)}
                              >
                                <Download size={16} />
                              </Button>

                              {/* Bouton Supprimer */}
                              <Button
                                variant="ghost"
                                size="sm"
                                className="flex items-center gap-2 text-red-600 hover:text-red-900"
                                onClick={() => handleDeleteDocument(doc.id, doc.type)}
                              >
                                <Trash2 size={16} />
                              </Button>

                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="bg-gray-50 py-10 border-2 border-dashed border-gray-300 rounded-lg flex flex-col items-center justify-center">
                        <FileText size={32} className="text-gray-400 mb-3" />
                        <p className="text-gray-500 mb-2">Aucun CV ajouté</p>
                        <Button
                          variant="outline"
                          onClick={() => {
                            const input = document.createElement('input');
                            input.type = 'file';
                            input.accept = 'application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document';
                            input.onchange = (e) => handleFileUpload(e, 'cv');
                            input.click();
                          }}
                          className="flex items-center gap-2 mt-2"
                        >
                          <Upload size={16} />
                          Ajouter un CV
                        </Button>
                      </div>
                    )}
                  </div>
                </div>

                {/* Section Diplômes et Certifications */}
                <div className="bg-white rounded-xl overflow-hidden shadow-sm border border-gray-200">
                  <div className="p-4 bg-gray-50 border-b border-gray-200 flex justify-between items-center">
                    <h3 className="font-semibold text-gray-700 flex items-center gap-2">
                      <BookOpen size={18} className="text-blue-500" />
                      Diplômes et Certifications
                    </h3>

                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        const input = document.createElement('input');
                        input.type = 'file';
                        input.multiple = true;
                        input.accept = 'application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document';
                        input.onchange = (e) => handleFileUpload(e, 'diplome');
                        input.click();
                      }}
                      className="flex items-center gap-2"
                    >
                      <Plus size={16} />
                      Ajouter
                    </Button>
                  </div>

                  <div className="p-5">
                    {documents.filter(doc => doc.type === 'diplome').length > 0 ? (
                      <div className="space-y-3">
                        {documents.filter(doc => doc.type === 'diplome').map((doc, index) => (



                          <div key={index} className="bg-gray-50 p-3 rounded-lg border border-gray-200 flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              {getDocumentIcon(doc)}
                              <div>
                                <p className="font-medium text-gray-800">{doc.nom_fichier || `Document ${index + 1}`}</p>
                                <div className="flex items-center gap-4 text-sm text-gray-500">
                                  <span>{doc.date_ajout ? new Date(doc.date_ajout).toLocaleDateString() : 'Date inconnue'}</span>
                                </div>
                              </div>
                            </div>
                            <div className="flex gap-2">
                              {/* Bouton Télécharger */}
                              <Button
                                variant="ghost"
                                size="sm"
                                className="flex items-center gap-2 text-blue-600 hover:text-blue-900"
                                onClick={() => handleDownloadDocument(doc.id, doc.nom_fichier)}
                              >
                                <Download size={16} />
                              </Button>

                              {/* Bouton Supprimer */}
                              <Button
                                variant="ghost"
                                size="sm"
                                className="flex items-center gap-2 text-red-600 hover:text-red-900"
                                onClick={() => handleDeleteDocument(doc.id, doc.type)}
                              >
                                <Trash2 size={16} />
                              </Button>

                            </div>




                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="bg-gray-50 py-10 border-2 border-dashed border-gray-300 rounded-lg flex flex-col items-center justify-center">
                        <BookOpen size={32} className="text-gray-400 mb-3" />
                        <p className="text-gray-500 mb-2">Aucun diplôme ajouté</p>
                        <Button
                          variant="outline"
                          onClick={() => {
                            const input = document.createElement('input');
                            input.type = 'file';
                            input.multiple = true;
                            input.accept = 'application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document';
                            input.onchange = (e) => handleFileUpload(e, 'diplome');
                            input.click();
                          }}
                          className="flex items-center gap-2 mt-2"
                        >
                          <Upload size={16} />
                          Ajouter des documents
                        </Button>
                      </div>
                    )}
                  </div>
                </div>

                {/* Section Pièce d'identité */}
                <div className="bg-white rounded-xl overflow-hidden shadow-sm border border-gray-200">
                  <div className="p-4 bg-gray-50 border-b border-gray-200 flex justify-between items-center">
                    <h3 className="font-semibold text-gray-700 flex items-center gap-2">
                      <IdCard size={18} className="text-orange-500" />
                      Pièce d'identité
                    </h3>

                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        const input = document.createElement('input');
                        input.type = 'file';
                        input.accept = 'application/pdf,image/jpeg,image/png,image/jpg';
                        input.onchange = (e) => handleFileUpload(e, 'piece_identite');
                        input.click();
                      }}
                      className="flex items-center gap-2"
                    >
                      <Plus size={16} />
                      Ajouter
                    </Button>
                  </div>

                  <div className="p-5">
                    {documents.filter(doc => doc.type === 'piece_identite').length > 0 ? (
                      <div className="space-y-3">
                        {documents.filter(doc => doc.type === 'piece_identite').map((doc, index) => (


                          <div key={index} className="bg-gray-50 p-3 rounded-lg border border-gray-200 flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              {getDocumentIcon(doc)}
                              <div>
                                <p className="font-medium text-gray-800">{doc.nom_fichier || `Document ${index + 1}`}</p>
                                <div className="flex items-center gap-4 text-sm text-gray-500">
                                  <span>{doc.date_ajout ? new Date(doc.date_ajout).toLocaleDateString() : 'Date inconnue'}</span>
                                </div>
                              </div>
                            </div>
                            <div className="flex gap-2">
                              {/* Bouton Télécharger */}
                              <Button
                                variant="ghost"
                                size="sm"
                                className="flex items-center gap-2 text-blue-600 hover:text-blue-900"
                                onClick={() => handleDownloadDocument(doc.id, doc.nom_fichier)}
                              >
                                <Download size={16} />
                              </Button>
                              {/* Bouton Supprimer */}
                              <Button
                                variant="ghost"
                                size="sm"
                                className="flex items-center gap-2 text-red-600 hover:text-red-900"
                                onClick={() => handleDeleteDocument(doc.id, doc.type)}
                              >
                                <Trash2 size={16} />
                              </Button>
                            </div>
                          </div>


                        ))}
                      </div>
                    ) : (
                      <div className="bg-gray-50 py-10 border-2 border-dashed border-gray-300 rounded-lg flex flex-col items-center justify-center">
                        <IdCard size={32} className="text-gray-400 mb-3" />
                        <p className="text-gray-500 mb-2">Aucune pièce d'identité ajoutée</p>
                        <Button
                          variant="outline"
                          onClick={() => {
                            const input = document.createElement('input');
                            input.type = 'file';
                            input.accept = 'application/pdf,image/jpeg,image/png,image/jpg';
                            input.onchange = (e) => handleFileUpload(e, 'piece_identite');
                            input.click();
                          }}
                          className="flex items-center gap-2 mt-2"
                        >
                          <Upload size={16} />
                          Ajouter une pièce d'identité
                        </Button>
                      </div>
                    )}
                  </div>
                </div>

                {/* Section Autres documents */}
                <div className="bg-white rounded-xl overflow-hidden shadow-sm border border-gray-200">
                  <div className="p-4 bg-gray-50 border-b border-gray-200 flex justify-between items-center">
                    <h3 className="font-semibold text-gray-700 flex items-center gap-2">
                      <File size={18} className="text-purple-500" />
                      Autres documents
                    </h3>

                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        const input = document.createElement('input');
                        input.type = 'file';
                        input.multiple = true;
                        input.accept = 'application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document';
                        input.onchange = (e) => handleFileUpload(e, 'other');
                        input.click();
                      }}
                      className="flex items-center gap-2"
                    >
                      <Plus size={16} />
                      Ajouter
                    </Button>
                  </div>

                  <div className="p-5">
                    {documents.filter(doc => doc.type !== 'contrat' && doc.type !== 'cv' && doc.type !== 'piece_identite' && doc.type !== 'diplome').length > 0 ? (
                      <div className="space-y-3">
                        {documents.filter(doc => doc.type !== 'contrat' && doc.type !== 'cv' && doc.type !== 'piece_identite' && doc.type !== 'diplome').map((doc, index) => (


                          <div key={index} className="bg-gray-50 p-3 rounded-lg border border-gray-200 flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              {getDocumentIcon(doc)}
                              <div>
                                <p className="font-medium text-gray-800">{doc.nom_fichier || `Document ${index + 1}`}</p>
                                <div className="flex items-center gap-4 text-sm text-gray-500">
                                  <span>{doc.date_ajout ? new Date(doc.date_ajout).toLocaleDateString() : 'Date inconnue'}</span>
                                </div>
                              </div>
                            </div>
                            <div className="flex gap-2">
                              {/* Bouton Télécharger */}
                              <Button
                                variant="ghost"
                                size="sm"
                                className="flex items-center gap-2 text-blue-600 hover:text-blue-900"
                                onClick={() => handleDownloadDocument(doc.id, doc.nom_fichier)}
                              >
                                <Download size={16} />
                              </Button>
                              {/* Bouton Supprimer */}
                              <Button
                                variant="ghost"
                                size="sm"
                                className="flex items-center gap-2 text-red-600 hover:text-red-900"
                                onClick={() => handleDeleteDocument(doc.id, doc.type)}
                              >
                                <Trash2 size={16} />
                              </Button>
                            </div>
                          </div>

                        ))}
                      </div>
                    ) : (
                      <div className="bg-gray-50 py-10 border-2 border-dashed border-gray-300 rounded-lg flex flex-col items-center justify-center">
                        <File size={32} className="text-gray-400 mb-3" />
                        <p className="text-gray-500 mb-2">Aucun document supplémentaire ajouté</p>
                        <Button
                          variant="outline"
                          onClick={() => {
                            const input = document.createElement('input');
                            input.type = 'file';
                            input.multiple = true;
                            input.accept = 'application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document';
                            input.onchange = (e) => handleFileUpload(e, 'other');
                            input.click();
                          }}
                          className="flex items-center gap-2 mt-2"
                        >
                          <Upload size={16} />
                          Ajouter des documents
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </Tabs.Content>


            {/* ============================================================================ */}
            {/* 🆕 ONGLET ENFANTS À CHARGE - VERSION CORPORATE */}
            {/* ============================================================================ */}
            {enfantsOuvert && (
            <Tabs.Content value="enfants" className="space-y-6 p-6">
              {/* 📌 EN-TÊTE SECTION */}
              <div className="bg-gradient-to-r from-[#3084b5] to-[#319190] rounded-lg p-6 text-white shadow-lg">
                <div className="flex items-start gap-4">
                  <div className="p-3 bg-white/20 rounded-lg backdrop-blur-sm">
                    <Users className="w-6 h-6 text-white" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-semibold mb-2">Gestion des Enfants à Charge</h3>
                    <p className="text-white/90 text-sm leading-relaxed">
                      Liez les élèves de l'établissement qui sont enfants de cet employé.
                      Les frais de scolarité seront automatiquement déduits du salaire mensuel une fois activés.
                    </p>
                  </div>
                </div>
              </div>

              {/* 🔍 SECTION RECHERCHE */}
              <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
                <div className="p-5 border-b border-gray-100">
                  <h4 className="text-sm font-semibold text-gray-700 tracking-wide mb-3">
                    Rechercher un Élève
                  </h4>

                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                    <input
                      type="text"
                      value={searchEleve}
                      onChange={(e) => handleSearchEleve(e.target.value)}
                      placeholder="Nom, prénom ou matricule de l'élève..."
                      className="w-full pl-11 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#3084b5] focus:border-transparent transition-all text-sm"
                    />
                    {isSearching && (
                      <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                        <div className="w-5 h-5 border-2 border-[#3084b5] border-t-transparent rounded-full animate-spin"></div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Résultats de recherche */}
                {elevesRecherche.length > 0 && (
                  <div className="p-4 bg-gray-50 max-h-64 overflow-y-auto">
                    <p className="text-xs font-medium text-gray-500 mb-3 tracking-wide">
                      {elevesRecherche.length} résultat(s) trouvé(s)
                    </p>
                    <div className="space-y-2">
                      {elevesRecherche.map(eleve => (
                        <button
                          key={eleve.id}
                          onClick={() => handleSelectEleve(eleve)}
                          className="w-full flex items-center justify-between p-3 bg-white rounded-lg border border-gray-200 hover:border-[#3084b5] hover:shadow-sm transition-all group"
                        >
                          <div className="flex items-center gap-3 flex-1">
                            <div className="w-10 h-10 bg-gradient-to-br from-[#3084b5] to-[#319190] rounded-full flex items-center justify-center text-white font-semibold text-sm">
                              {eleve.prenom?.charAt(0)}{eleve.nom?.charAt(0)}
                            </div>
                            <div className="text-left">
                              <p className="font-medium text-gray-800 text-sm">
                                {eleve.nom} {eleve.prenom}
                              </p>
                              <p className="text-xs text-gray-500 flex items-center gap-2">
                                <span>{eleve.matricule}</span>
                                {eleve.classe_nom && (
                                  <>
                                    <span className="text-gray-300">•</span>
                                    <span>{eleve.classe_nom}</span>
                                  </>
                                )}
                              </p>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="text-sm font-semibold text-[#3084b5]">
                              {parseFloat(eleve.frais_mensuel || 0).toLocaleString('fr-FR')} FCFA
                            </p>
                            <p className="text-xs text-gray-500">par mois</p>
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {searchEleve.length >= 2 && elevesRecherche.length === 0 && !isSearching && (
                  <div className="p-8 text-center">
                    <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
                      <Search className="w-6 h-6 text-gray-400" />
                    </div>
                    <p className="text-sm text-gray-500">Aucun élève trouvé</p>
                  </div>
                )}
              </div>

              {/* 📋 ZONE DE SÉLECTION TEMPORAIRE */}
              {selectedEleves.length > 0 && (
                <div className="bg-blue-50 border-2 border-dashed border-[#3084b5] rounded-lg p-5">
                  <div className="flex items-center justify-between mb-4">
                    <h4 className="text-sm font-semibold text-gray-700 tracking-wide">
                      Élèves Sélectionnés ({selectedEleves.length})
                    </h4>
                    <button
                      onClick={handleLierEleves}
                      className="inline-flex items-center gap-2 px-4 py-2 bg-[#3084b5] text-white rounded-lg hover:bg-[#2a6d9a] transition-colors text-sm font-medium shadow-sm"
                    >
                      <Plus className="w-4 h-4" />
                      Lier à cet employé
                    </button>
                  </div>

                  <div className="space-y-2">
                    {selectedEleves.map(eleve => (
                      <div
                        key={eleve.id}
                        className="flex items-center justify-between p-3 bg-white rounded-lg border border-blue-200"
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 bg-gradient-to-br from-[#3084b5] to-[#319190] rounded-full flex items-center justify-center text-white font-semibold text-xs">
                            {eleve.prenom?.charAt(0)}{eleve.nom?.charAt(0)}
                          </div>
                          <div>
                            <p className="font-medium text-gray-800 text-sm">
                              {eleve.nom} {eleve.prenom}
                            </p>
                            <p className="text-xs text-gray-500">{eleve.classe_nom}</p>
                          </div>
                        </div>
                        <button
                          onClick={() => handleRemoveFromSelection(eleve.id)}
                          className="p-1.5 hover:bg-red-50 rounded-lg transition-colors group"
                        >
                          <XCircle className="w-5 h-5 text-gray-400 group-hover:text-red-500" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* 👥 LISTE COMPACTE DES ENFANTS LIÉS */}
              <div>
                <h4 className="text-sm font-semibold text-gray-700 tracking-wide mb-4">
                  Enfants Liés ({enfantsACharge.length})
                </h4>

                {isLoadingEnfants ? (
                  <div className="flex justify-center items-center py-12">
                    <div className="w-10 h-10 border-4 border-[#3084b5] border-t-transparent rounded-full animate-spin"></div>
                  </div>
                ) : enfantsACharge.length === 0 ? (
                  <div className="bg-gray-50 rounded-lg border-2 border-dashed border-gray-200 p-12 text-center">
                    <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Users className="w-8 h-8 text-gray-300" />
                    </div>
                    <p className="text-gray-700 font-medium mb-2">Aucun enfant lié</p>
                    <p className="text-sm text-gray-500 flex items-center justify-center gap-2">
                      <ArrowUp className="w-4 h-4" />
                      Utilisez la barre de recherche ci-dessus
                    </p>
                  </div>
                ) : (
                  <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
                    {/* En-tête du tableau */}
                    <div className="grid grid-cols-12 gap-4 px-4 py-3 bg-gray-50 border-b border-gray-200 text-xs font-semibold text-gray-600 uppercase tracking-wide">
                      <div className="col-span-4">Élève</div>
                      <div className="col-span-2">Matricule</div>
                      <div className="col-span-2">Classe</div>
                      <div className="col-span-2 text-right">Frais mensuel</div>
                      <div className="col-span-2 text-center">Actions</div>
                    </div>

                    {/* Corps du tableau */}
                    <div className="divide-y divide-gray-100">
                      {enfantsACharge.map(enfant => (
                        <div key={enfant.id}>
                          {/* Ligne principale */}
                          <div className="grid grid-cols-12 gap-4 px-4 py-3 hover:bg-gray-50 transition-colors items-center">
                            {/* Nom complet */}
                            <div className="col-span-4 flex items-center gap-2">
                              <div className="w-8 h-8 bg-gradient-to-br from-[#3084b5] to-[#319190] rounded-full flex items-center justify-center text-white font-semibold text-xs flex-shrink-0">
                                <User className="w-4 h-4" />
                              </div>
                              <span className="font-medium text-gray-800 text-sm">
                                {enfant.eleve_nom} {enfant.eleve_prenom}
                              </span>
                            </div>

                            {/* Matricule */}
                            <div className="col-span-2 text-sm text-gray-600">
                              {enfant.eleve_matricule}
                            </div>

                            {/* Classe */}
                            <div className="col-span-2">
                              {enfant.classe_nom ? (
                                <span className="text-sm text-gray-600">
                                  {enfant.classe_nom}
                                </span>
                              ) : (
                                <span className="text-sm text-gray-400">-</span>
                              )}
                              {/* Liaison restée sur la fiche d'une autre année :
                                  l'enfant n'est pas (encore) réinscrit dans
                                  l'année active, classe et frais sont anciens. */}
                              {Number(enfant.fiche_annee_active) !== 1 && enfant.eleve_annee_nom && (
                                <p className="text-xs text-[#c66d59] mt-0.5">
                                  Fiche {enfant.eleve_annee_nom}
                                </p>
                              )}
                            </div>

                            {/* Montant */}
                            <div className="col-span-2 text-right">
                              <span className="font-semibold text-[#3084b5]">
                                {parseFloat(enfant.frais_mensuel || 0).toLocaleString('fr-FR')}
                              </span>
                              <span className="text-xs text-gray-500 ml-1">FCFA</span>
                            </div>

                            {/* Actions */}
                            <div className="col-span-2 flex items-center justify-center gap-2">
                              {/* La retenue est un mouvement de PAIE, pas de
                                  comptabilité : une école qui facture des frais
                                  sans gérer la paie accorde la réduction, mais
                                  ne la prélève pas sur le salaire.
                                  `deduction_active` naît à 0, donc masquer cet
                                  interrupteur ne laisse aucune retenue latente. */}
                              {retenueSurSalaireOuverte && (
                                <button
                                  onClick={() => handleToggleDeduction(enfant.id)}
                                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md font-medium text-xs transition-all ${enfant.deduction_active
                                      ? 'bg-[#319190]/10 text-[#319190] border border-[#319190] hover:bg-[#319190]/20'
                                      : 'bg-gray-50 text-gray-600 border border-gray-200 hover:bg-gray-100'
                                    }`}
                                  title={enfant.deduction_active ? 'Déduction active' : 'Déduction inactive'}
                                >
                                  {enfant.deduction_active ? (
                                    <CheckCircle className="w-3.5 h-3.5 text-[#319190]" />
                                  ) : (
                                    <XCircle className="w-3.5 h-3.5 text-gray-400" />
                                  )}
                                  {enfant.deduction_active ? 'Active' : 'Inactive'}
                                </button>
                              )}

                              <button
                                onClick={() => handleDelierEnfant(enfant.id)}
                                className="p-1.5 hover:bg-[#c66d59]/10 rounded-md transition-colors group"
                                title="Supprimer la liaison"
                              >
                                <Trash2 className="w-4 h-4 text-gray-400 group-hover:text-[#c66d59]" />
                              </button>
                            </div>
                          </div>

                          {/* Footer avec infos de traçabilité */}
                          <div className="px-4 py-2 bg-gradient-to-r from-gray-50 to-blue-50/30 border-t border-gray-100">
                            <div className="flex items-start gap-2">
                              <div className="mt-0.5">
                                <svg className="w-3.5 h-3.5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                              </div>

                              <div className="flex-1 text-xs text-gray-600 leading-relaxed">
                                {enfant.created_by_nom && enfant.created_by_prenom ? (
                                  <p>
                                    Cette liaison a été mise en place par{' '}
                                    <span className="font-semibold text-[#3084b5]">
                                      {enfant.created_by_prenom} {enfant.created_by_nom}
                                    </span>
                                    {' '}le{' '}
                                    <span className="font-medium">
                                      {new Date(enfant.created_at).toLocaleDateString('fr-FR', {
                                        day: '2-digit',
                                        month: 'long',
                                        year: 'numeric'
                                      })}
                                    </span>
                                    {' '}à{' '}
                                    <span className="font-medium">
                                      {new Date(enfant.created_at).toLocaleTimeString('fr-FR', {
                                        hour: '2-digit',
                                        minute: '2-digit'
                                      })}
                                    </span>
                                  </p>
                                ) : (
                                  <p>
                                    Liaison créée le{' '}
                                    {new Date(enfant.date_liaison).toLocaleDateString('fr-FR', {
                                      day: '2-digit',
                                      month: 'long',
                                      year: 'numeric'
                                    })}
                                  </p>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Footer informatif compact */}
                    <div className="px-4 py-2 bg-gray-50 border-t border-gray-200">
                      <p className="text-xs text-gray-500">
                        💡 Seules les déductions actives seront retenues sur le salaire
                      </p>
                    </div>
                  </div>
                )}
              </div>


              {/* 🌐 ENFANTS INTER-SITES (lecture seule) */}
              {(enfantsInterSite.length > 0 || isLoadingEnfantsInterSite) && (
                <div>
                  <h4 className="text-sm font-semibold text-gray-700 tracking-wide mb-4 flex items-center gap-2">
                    <Network size={18} className="text-gray-600" />
                    Enfants sur d'autres sites ({enfantsInterSite.length})
                  </h4>

                  {isLoadingEnfantsInterSite ? (
                    <div className="flex justify-center items-center py-8">
                      <div className="w-8 h-8 border-4 border-teal-500 border-t-transparent rounded-full animate-spin"></div>
                    </div>
                  ) : (
                    <div className="bg-white rounded-lg border border-teal-200 shadow-sm overflow-hidden">
                      {/* En-tête */}
                      <div className="grid grid-cols-12 gap-4 px-4 py-3 bg-teal-50 border-b border-teal-200 text-xs font-semibold text-teal-700 uppercase tracking-wide">
                        <div className="col-span-4">Élève</div>
                        <div className="col-span-2">Classe</div>
                        <div className="col-span-3">Site</div>
                        <div className="col-span-2 text-right">Frais mensuel</div>
                        <div className="col-span-1 text-center">Statut</div>
                      </div>

                      {/* Corps */}
                      <div className="divide-y divide-teal-100">
                        {enfantsInterSite.map(enfant => (
                          <div key={`intersite-${enfant.id}`}>
                            <div className="grid grid-cols-12 gap-4 px-4 py-3 hover:bg-teal-50/50 transition-colors items-center">
                              {/* Nom */}
                              <div className="col-span-4 flex items-center gap-2">
                                <div className="w-8 h-8 bg-gradient-to-br from-teal-500 to-teal-700 rounded-full flex items-center justify-center text-white flex-shrink-0">
                                  <User size={16} className="text-white" />
                                </div>
                                <span className="font-medium text-gray-800 text-sm">
                                  {enfant.child_name}
                                </span>
                              </div>

                              {/* Classe */}
                              <div className="col-span-2 text-sm text-gray-600">
                                {enfant.child_classe || '-'}
                              </div>

                              {/* Site */}
                              <div className="col-span-3">
                                <div className="flex items-center gap-1.5">
                                  <Building className="w-3.5 h-3.5 text-teal-600" />
                                  <span className="text-sm text-teal-700 font-medium">
                                    {enfant.child_site_subdomain?.toUpperCase()}
                                  </span>
                                </div>
                              </div>

                              {/* Montant */}
                              <div className="col-span-2 text-right">
                                <span className="font-semibold text-teal-600">
                                  {parseFloat(enfant.montant_mensuel || 0).toLocaleString('fr-FR')}
                                </span>
                                <span className="text-xs text-gray-500 ml-1">FCFA</span>
                              </div>

                              {/* Statut */}
                              <div className="col-span-1 flex justify-center">
                                <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium bg-teal-100 text-teal-700 border border-teal-200">
                                  <CheckCircle className="w-3 h-3" />
                                  Active
                                </span>
                              </div>
                            </div>

                            {/* Footer info */}
                            <div className="px-4 py-2 bg-gradient-to-r from-teal-50/50 to-gray-50 border-t border-teal-100">
                              <div className="flex items-center gap-2 text-xs text-gray-500">
                                <svg className="w-3.5 h-3.5 text-teal-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                                <span>
                                  Liaison inter-sites - Géré depuis le module{' '}
                                  <span className="font-semibold text-teal-600">Liaison Inter-Sites</span>
                                </span>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>

                      {/* Footer informatif */}
                      <div className="px-4 py-2 bg-teal-50 border-t border-teal-200">
                        <p className="text-xs text-teal-600">
                          Ces liaisons sont gérées via le module Liaison Inter-Sites. Les retenues sont automatiquement déduites lors du paiement du salaire.
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              )}



              {/* 💰 RÉCAPITULATIF TOTAL COMPACT */}
              {calculerTotalRetenues() > 0 && (
                <div className="bg-gradient-to-r from-[#3084b5] to-[#319190] rounded-lg p-4 text-white shadow-md">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-white/20 rounded-lg backdrop-blur-sm">
                        <CreditCard className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <p className="text-white/80 text-xs font-medium uppercase tracking-wide">
                          Total des Retenues Mensuelles
                        </p>
                        <p className="text-2xl font-bold mt-0.5">
                          {calculerTotalRetenues().toLocaleString('fr-FR')} FCFA
                        </p>
                      </div>
                    </div>
                    <div className="text-right text-xs text-white/90 max-w-xs space-y-1">
                      {enfantsACharge.filter(e => e.deduction_active === 1).length > 0 && (
                        <p>{enfantsACharge.filter(e => e.deduction_active === 1).length} enfant(s) sur ce site</p>
                      )}
                      {enfantsInterSite.length > 0 && (
                        <p>{enfantsInterSite.length} enfant(s) Liaison inter-sites</p>
                      )}
                    </div>
                  </div>
                </div>
              )}


            </Tabs.Content>
            )}

            {/* Onglet Accès et sécurité */}
            <Tabs.Content value="access">
              <div className="p-5 space-y-6 pb-10">
                {/* Section statut d'accès */}
                <div className="bg-white rounded-xl overflow-hidden shadow-sm border border-gray-200">
                  <div className="p-4 bg-[#246990] text-white border-b border-[#246990]">
                    <h3 className="font-semibold flex items-center gap-2">
                      <Lock size={18} />
                      Statut d'accès à l'application
                    </h3>
                  </div>

                  <div className="p-6">
                    {localEmployee.acces_application || localEmployee.info_pro_acces_application === 1 ? (
                      <div className="space-y-6">
                        {/* Employé ayant déjà accès */}
                        <div className="flex items-center gap-4 p-4 bg-green-50 rounded-lg border border-green-200">
                          <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center">
                            <CheckCircle size={24} className="text-white" />
                          </div>
                          <div>
                            <h4 className="font-semibold text-green-700">Accès autorisé</h4>
                            <p className="text-green-600 text-sm">Cet employé peut se connecter à l'application</p>
                          </div>
                        </div>

                        {/* Informations d'accès */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                          <div className="space-y-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-600 mb-1">
                                Identifiant de connexion
                              </label>
                              <div className="bg-gray-50 p-3 rounded-lg border border-gray-200 font-mono text-gray-800">
                                {localEmployee.identifiant || localEmployee.email || 'Non défini'}
                              </div>
                            </div>

                            <div>
                              <label className="block text-sm font-medium text-gray-600 mb-1">
                                Dernière connexion
                              </label>
                              <div className="bg-gray-50 p-3 rounded-lg border border-gray-200">
                                {/* MODIFIÉ : Utiliser accessData.lastConnection au lieu du premier élément de l'historique */}
                                {accessData.lastConnection
                                  ? accessData.lastConnection.date_connexion_formattee
                                  : 'Aucune connexion enregistrée'
                                }
                              </div>
                            </div>
                          </div>
                          {/* Bouton réinitialisation */}
                          <div className="space-y-4 flex flex-col justify-center">
                            {!accessData.demandePendante && !(localEmployee.shouldShowPassword && localEmployee.motDePasseClair) && (
                              <div className="flex justify-center">
                                <Button
                                  variant="outline"
                                  onClick={() => {
                                    setTypeDemande('reinitialisation');
                                    setIsDemandeModalOpen(true);
                                  }}
                                  className="flex items-center justify-center gap-2 border-orange-300 text-orange-700 hover:bg-orange-50 px-5 py-2 h-auto rounded-lg"
                                >
                                  <Key size={16} />
                                  Demander une réinitialisation du mot de passe
                                </Button>
                              </div>
                            )}

                            {/* Suspension d'acces.
                                Le droit RESSOURCE_HUMAINES.ACCES.MANAGE existait
                                deja au catalogue, nomme pour exactement cela, et
                                ne gardait rien : c'est ici sa porte. */}
                            <ProtectedComponent module="RESSOURCE_HUMAINES" sousModule="ACCES" action="MANAGE">
                              {Number(localEmployee.acces_suspendu) === 1 ? (
                                <div className="rounded-lg border border-red-200 bg-red-50 p-4">
                                  <div className="flex items-center gap-2 mb-1">
                                    <Lock size={16} className="text-red-600" />
                                    <span className="font-semibold text-red-800">Accès suspendu</span>
                                  </div>
                                  {localEmployee.acces_suspendu_motif && (
                                    <p className="text-sm text-red-700">{localEmployee.acces_suspendu_motif}</p>
                                  )}
                                  {localEmployee.acces_suspendu_par && (
                                    <p className="text-xs text-red-600 mt-0.5">
                                      Par {localEmployee.acces_suspendu_par}
                                    </p>
                                  )}
                                  <Button
                                    variant="outline"
                                    disabled={suspensionEnCours}
                                    onClick={() => basculerAcces(false)}
                                    className="mt-3 flex items-center gap-2 border-green-300 text-green-700 hover:bg-green-50 px-5 py-2 h-auto rounded-lg"
                                  >
                                    <Unlock size={16} />
                                    {suspensionEnCours ? 'Rétablissement…' : "Rétablir l'accès"}
                                  </Button>
                                </div>
                              ) : panneauSuspension ? (
                                <div className="rounded-lg border border-red-200 bg-red-50 p-4">
                                  <p className="text-sm font-medium text-red-800 mb-2">
                                    Pourquoi suspendre cet accès ?
                                  </p>
                                  <Input
                                    value={motifSuspension}
                                    onChange={(e) => setMotifSuspension(e.target.value)}
                                    placeholder="Motif (obligatoire)"
                                    disabled={suspensionEnCours}
                                    className="h-9 text-sm"
                                  />
                                  <p className="mt-1.5 text-xs text-red-700">
                                    Une suspension se lève souvent par quelqu'un d'autre, des semaines
                                    plus tard : sans motif, on ne sait plus s'il faut rouvrir.
                                  </p>
                                  <div className="mt-3 flex flex-wrap gap-2">
                                    <Button
                                      disabled={suspensionEnCours || motifSuspension.trim().length < 5}
                                      onClick={() => basculerAcces(true)}
                                      className="flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white px-5 py-2 h-auto rounded-lg"
                                    >
                                      <Lock size={16} />
                                      {suspensionEnCours ? 'Suspension…' : "Suspendre l'accès"}
                                    </Button>
                                    <Button
                                      variant="outline"
                                      disabled={suspensionEnCours}
                                      onClick={() => { setPanneauSuspension(false); setMotifSuspension(''); }}
                                      className="px-5 py-2 h-auto rounded-lg"
                                    >
                                      Annuler
                                    </Button>
                                  </div>
                                </div>
                              ) : (
                                <div className="flex justify-center">
                                  <Button
                                    variant="outline"
                                    onClick={() => setPanneauSuspension(true)}
                                    className="flex items-center justify-center gap-2 border-red-300 text-red-700 hover:bg-red-50 px-5 py-2 h-auto rounded-lg"
                                  >
                                    <Lock size={16} />
                                    Suspendre l'accès
                                  </Button>
                                </div>
                              )}
                            </ProtectedComponent>
                          </div>


                          <div className="space-y-4">
                            {/* Mot de passe initial (à changer) */}
                            {localEmployee.shouldShowPassword && localEmployee.motDePasseClair && (
                              <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                                <div className="flex items-center gap-2 mb-2">
                                  <AlertTriangle size={18} className="text-orange-600" />
                                  <span className="font-medium text-orange-800">Mot de passe initial à transmettre</span>
                                </div>

                                {/* NOUVEAU : Informations sur la demande traitée */}
                                {localEmployee.demandeAcceptee && (
                                  <div className="mb-3 text-xs text-orange-700 bg-orange-100 p-2 rounded border-l-4 border-orange-300">
                                    <p><strong>Demande acceptée le :</strong> {formatDate(localEmployee.demandeAcceptee.date_traitement)}</p>
                                    <p><strong>Demande faite le :</strong> {formatDate(localEmployee.demandeAcceptee.date_demande)}</p>
                                    <p><strong>Traité par :</strong> {localEmployee.demandeAcceptee.traite_par_nom_complet || 'Support'}</p>
                                    <p><strong>Traité en :</strong> {calculateProcessingDelay(localEmployee.demandeAcceptee.date_demande, localEmployee.demandeAcceptee.date_traitement) || 'N/A'}</p>
                                  </div>
                                )}

                                <p className="text-orange-700 text-sm mb-3">
                                  Voici le mot de passe à transmettre à cet employé
                                </p>
                                <div className="flex items-center gap-3">
                                  <div className="bg-white p-2 rounded border flex-1">
                                    <span className="font-mono text-gray-800 font-semibold">
                                      {showTemporaryPassword
                                        ? localEmployee.motDePasseClair
                                        : '••••••••••••'}
                                    </span>
                                  </div>
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => setShowTemporaryPassword(!showTemporaryPassword)}
                                    className="flex items-center gap-2"
                                  >
                                    {showTemporaryPassword ? (
                                      <>
                                        <EyeOff size={16} />
                                        Masquer
                                      </>
                                    ) : (
                                      <>
                                        <Eye size={16} />
                                        Afficher
                                      </>
                                    )}
                                  </Button>
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => {
                                      navigator.clipboard.writeText(localEmployee.motDePasseClair);
                                      showSuccessToast('Mot de passe copié dans le presse-papier');
                                    }}
                                    className="flex items-center gap-2"
                                  >
                                    <Copy size={16} />
                                    Copier
                                  </Button>
                                </div>
                                <p className="text-xs text-orange-600 mt-2">
                                  Ce mot de passe devra être changé lors de la première connexion de l'employé
                                </p>
                              </div>
                            )}

                            {/* Demande en cours */}
                            {accessData.demandePendante && accessData.demandePendante.type_demande === 'reinitialisation' && (
                              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                                <div className="flex items-center gap-2 mb-2">
                                  <Clock size={18} className="text-blue-600" />
                                  <span className="font-medium text-blue-800">Demande de réinitialisation en cours</span>
                                </div>
                                <p className="text-blue-700 text-sm mb-2">
                                  Demandée par : {accessData.demandePendante.demandeur_prenom} {accessData.demandePendante.demandeur_nom}
                                </p>
                                <p className="text-blue-700 text-sm">
                                  Le support a 1h pour traiter cette demande.
                                </p>
                                <div className="text-xs text-blue-600 mt-2">
                                  Demande effectuée le {formatCreationDateTime(accessData.demandePendante.date_demande)}
                                </div>
                              </div>
                            )}

                            {/* Bouton réinitialisation */}




                            {accessData.demandeRefusee && (
                              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center gap-2 mb-2">
                                    <XCircle size={18} className="text-red-600" />
                                    <span className="font-medium text-red-800">Demande d'accès refusée</span>
                                  </div>
                                  <button
                                    onClick={() => {
                                      setConfirmAction('close');
                                      setIsConfirmDialogOpen(true);
                                    }}
                                    className="text-gray-400 hover:text-gray-600 ml-2"
                                  >
                                    <X size={20} />
                                  </button>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                                  <div>
                                    <strong>Type :</strong> {accessData.demandeRefusee.type_demande === 'creation' ? 'Création d\'accès' : 'Réinitialisation'}
                                    <br />
                                    <strong>Date de demande :</strong> {formatDate(accessData.demandeRefusee.date_demande)}
                                    <br />
                                    <strong>Date de refus :</strong> {formatDate(accessData.demandeRefusee.date_traitement)}
                                    <br />
                                    <span className="text-xs text-gray-600">
                                      <strong>Traité en :</strong> {calculateProcessingDelay(accessData.demandeRefusee.date_demande, accessData.demandeRefusee.date_traitement) || 'N/A'}
                                    </span>
                                  </div>
                                  <div>
                                    <strong>Traité par :</strong> {
                                      accessData.demandeRefusee.nom_complet_traite_par || 'Support'
                                    }
                                  </div>
                                </div>

                                {accessData.demandeRefusee.notes_refus && (
                                  <div className="mt-3 p-3 bg-white rounded border">
                                    <strong className="text-red-800">Motif du refus :</strong>
                                    <p className="text-gray-700 mt-1">{accessData.demandeRefusee.notes_refus}</p>
                                  </div>
                                )}

                                <div className="mt-3 flex items-center justify-between">
                                  <div className="text-xs text-red-600">
                                    Contactez le support pour plus d'informations ou pour faire une nouvelle demande.
                                  </div>
                                  <button
                                    onClick={() => {
                                      setConfirmAction('acknowledge');
                                      setIsConfirmDialogOpen(true);
                                    }}
                                    className="bg-[#246990] hover:bg-[#246990]/90 text-white px-4 py-2 rounded text-sm"
                                  >
                                    J'ai pris connaissance
                                  </button>
                                </div>
                              </div>
                            )}

                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="space-y-6">
                        {/* Employé sans accès */}
                        <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
                          <div className="w-12 h-12 bg-gray-500 rounded-full flex items-center justify-center">
                            <Lock size={24} className="text-white" />
                          </div>
                          <div>
                            <h4 className="font-semibold text-gray-700">Pas d'accès à l'application</h4>
                            <p className="text-gray-600 text-sm">Cet employé ne dispose pas encore d'un accès à l'application</p>
                          </div>
                        </div>

                        {/* Demande en cours ou bouton de demande */}
                        {accessData.demandePendante && accessData.demandePendante.type_demande === 'creation' ? (
                          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                            <div className="flex items-center gap-2 mb-2">
                              <Clock size={18} className="text-blue-600" />
                              <span className="font-medium text-blue-800">Demande de création d'accès en cours</span>
                            </div>
                            <p className="text-blue-700 text-sm mb-2">
                              Demandée par : {accessData.demandePendante.demandeur_prenom} {accessData.demandePendante.demandeur_nom}
                            </p>
                            <p className="text-blue-700 text-sm">
                              Le support a 1h pour créer l'accès.
                            </p>
                            <div className="text-xs text-blue-600 mt-2">
                              Demande effectuée le {formatCreationDateTime(accessData.demandePendante.date_demande)}
                            </div>
                          </div>
                        ) : (
                          <div className="text-center py-6">
                            <Button
                              onClick={() => {
                                setTypeDemande('creation');
                                setIsDemandeModalOpen(true);
                              }}
                              className="bg-[#246990] hover:bg-[#246990]/90 text-white px-6 py-3 text-base"
                            >
                              <Plus size={18} className="mr-2" />
                              Faire une demande de création d'accès
                            </Button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>




                {/* Section historique des connexions - Version finale simplifiée */}
                <div className="bg-white rounded-xl overflow-hidden shadow-sm border border-gray-200">
                  <div className="p-4 bg-gray-50 border-b border-gray-200">
                    <h3 className="font-semibold text-gray-700 flex items-center gap-2">
                      <Clock size={18} className="text-gray-600" />
                      Historique des connexions
                    </h3>
                  </div>

                  <div className="p-5">
                    {accessData.historiqueConnexions && accessData.historiqueConnexions.length > 0 ? (
                      <div className="space-y-4">

                        <div className="overflow-x-auto">
                          <table className="w-full border-collapse">
                            <thead>
                              <tr className="border-b border-gray-200 bg-gray-50">
                                {/* Date et heure à l'extrême gauche */}
                                <th className="text-left p-3 font-medium text-gray-700">Date et heure</th>
                                {/* Statut à l'extrême droite */}
                                <th className="text-right p-3 font-medium text-gray-700">Statut</th>
                              </tr>
                            </thead>
                            <tbody>
                              {accessData.historiqueConnexions.map((connexion, index) => (
                                <tr key={index} className="border-b border-gray-100 hover:bg-gray-50">
                                  {/* Date alignée à gauche */}
                                  <td className="p-3 text-gray-800 text-left">
                                    {connexion.date_connexion_formattee}
                                  </td>
                                  {/* Statut aligné à droite avec badge de taille fixe */}
                                  <td className="p-3 text-right">
                                    <span className={`inline-block w-20 text-center px-2 py-1 rounded-full text-xs font-medium ${connexion.statut === 'succes'
                                        ? 'bg-green-100 text-green-800'
                                        : connexion.statut === 'echec'
                                          ? 'bg-red-100 text-red-800'
                                          : 'bg-orange-100 text-orange-800'
                                      }`}>
                                      {connexion.statut === 'succes' ? 'Réussie' :
                                        connexion.statut === 'echec' ? 'Échec' : 'Tentative'}
                                    </span>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>

                        {/* Pagination */}
                        {connectionHistoryPagination.totalPages > 1 && (
                          <div className="flex justify-between items-center mt-6 pt-4 border-t border-gray-200">
                            <div className="text-sm text-gray-600">
                              {connectionHistoryPagination.total} connexion{connectionHistoryPagination.total > 1 ? 's' : ''} au total
                            </div>
                            <div className="flex items-center gap-4">
                              <Button
                                variant="outline"
                                size="sm"
                                disabled={!connectionHistoryPagination.hasPrev}
                                onClick={() => loadConnectionHistory(connectionHistoryPagination.currentPage - 1)}
                              >
                                Précédent
                              </Button>
                              <span className="text-sm text-gray-600">
                                Page {connectionHistoryPagination.currentPage} sur {connectionHistoryPagination.totalPages}
                              </span>
                              <Button
                                variant="outline"
                                size="sm"
                                disabled={!connectionHistoryPagination.hasNext}
                                onClick={() => loadConnectionHistory(connectionHistoryPagination.currentPage + 1)}
                              >
                                Suivant
                              </Button>
                            </div>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="text-center py-8 text-gray-500">
                        <Clock size={32} className="mx-auto text-gray-300 mb-2" />
                        <p>Aucun historique de connexion disponible</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>








            </Tabs.Content>

            {/* Rendu seulement quand l'onglet est ouvert : le code d'accès
                tout juste émis ne vit que dans ce composant, et quitter
                l'onglet le fait disparaître, comme le dit l'écran. */}
            {espaceEnseignantOuvert && (
              <Tabs.Content value="acces_enseignant">
                {activeTab === 'acces_enseignant' && (
                  <AccesEnseignantEmploye
                    employeId={employee.id}
                    onToast={(message, type) => onShowToast?.({ message, type })}
                  />
                )}
              </Tabs.Content>
            )}

            <Tabs.Content value="profile_history" className="p-6">
              <div className="space-y-5">
                <div className="bg-white rounded-lg overflow-hidden shadow-sm border border-gray-200">
                  <div className="p-4 bg-gray-50 border-b border-gray-200">
                    <h3 className="font-semibold text-gray-700 flex items-center gap-2">
                      <History size={18} className="text-[#246990]" />
                      Historique des modifications RH
                    </h3>
                  </div>

                  <div className="p-5">
                    {isLoadingProfileChangeHistory ? (
                      <div className="flex items-center justify-center gap-2 py-10 text-gray-600">
                        <RefreshCw size={18} className="animate-spin" />
                        Chargement de l historique...
                      </div>
                    ) : profileChangeHistory.length === 0 ? (
                      <div className="text-center py-10 text-gray-500">
                        <History size={34} className="mx-auto text-gray-300 mb-2" />
                        <p>Aucune demande de modification RH enregistrée</p>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {profileChangeHistory.map((entry) => {
                          const status = getProfileHistoryStatus(entry.statut);
                          const changes = getProfileHistoryChanges(entry);

                          return (
                            <div key={entry.id} className="rounded-lg border border-gray-200 bg-white p-4">
                              <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
                                <div>
                                  <div className="flex flex-wrap items-center gap-2">
                                    <span className={`inline-flex items-center rounded-full border px-3 py-1 text-xs font-semibold ${status.className}`}>
                                      {status.label}
                                    </span>
                                    <span className="text-sm font-semibold text-gray-800">
                                      {entry.support_titre || 'Modification des informations personnelles'}
                                    </span>
                                  </div>
                                  <div className="mt-2 text-sm text-gray-600">
                                    Demandée par {formatHistoryPerson(entry.demandeur_prenom, entry.demandeur_nom)} le {formatCreationDateTime(entry.date_demande)}
                                  </div>
                                  {entry.date_validation && (
                                    <div className="mt-1 text-sm text-gray-600">
                                      Décision par {formatHistoryPerson(entry.validateur_prenom, entry.validateur_nom)} le {formatCreationDateTime(entry.date_validation)}
                                    </div>
                                  )}
                                </div>
                              </div>

                              {entry.motif && (
                                <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
                                  <span className="font-semibold">Motif :</span> {entry.motif}
                                </div>
                              )}

                              <div className="mt-4 overflow-x-auto rounded-lg border border-gray-200">
                                <table className="w-full text-sm">
                                  <thead className="bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-500">
                                    <tr>
                                      <th className="px-4 py-3">Champ</th>
                                      <th className="px-4 py-3">Ancienne valeur</th>
                                      <th className="px-4 py-3">Nouvelle valeur</th>
                                    </tr>
                                  </thead>
                                  <tbody className="divide-y divide-gray-100">
                                    {changes.length > 0 ? changes.map(([field, change]) => (
                                      <tr key={field}>
                                        <td className="px-4 py-3 font-semibold text-gray-800">{change?.label || field.replace(/_/g, ' ')}</td>
                                        <td className="px-4 py-3 text-gray-600">{change?.ancienne_valeur || '-'}</td>
                                        <td className="px-4 py-3 font-semibold text-[#246990]">{change?.nouvelle_valeur || '-'}</td>
                                      </tr>
                                    )) : (
                                      <tr>
                                        <td colSpan={3} className="px-4 py-5 text-center text-gray-500">Pièce d identité ou justificatif uniquement.</td>
                                      </tr>
                                    )}
                                  </tbody>
                                </table>
                              </div>

                              {entry.commentaire_decision && (
                                <div className="mt-4 rounded-lg border border-gray-200 bg-gray-50 p-3 text-sm text-gray-700">
                                  <span className="font-semibold">Commentaire support :</span> {entry.commentaire_decision}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </Tabs.Content>
            {/* Pied de page fixe */}
            <div className="sticky bottom-0 bg-white border-t border-gray-200 p-4 flex justify-between items-center z-10">
              {activeTab === 'details' && (
                <>
                  {isEditing ? (
                    <div className="flex space-x-3">
                      <Button
                        variant="outline"
                        onClick={handleCancel}
                        className="flex items-center gap-2 text-base px-5 py-2 h-auto"
                      >
                        <X size={18} />
                        Annuler
                      </Button>
                      <Button
                        onClick={handleSave}
                        className="flex items-center gap-2 bg-[#319190] hover:bg-[#63b5b4]/90 text-white text-base px-5 py-2 h-auto rounded-lg"
                      >
                        <Save size={18} />
                        Enregistrer
                      </Button>
                    </div>
                  ) : droitsFiche.peutModifier && (
                    <Button
                      onClick={() => {
                        // Récupérer les dates actuelles
                        const dateEmbauche = localEmployee.date_embauche;
                        const dateNaissance = localEmployee.date_naissance;

                        // Formater les dates si nécessaire
                        const formattedDateEmbauche = formatDateForInput(dateEmbauche);
                        const formattedDateNaissance = formatDateForInput(dateNaissance);

                        // Mettre à jour l'état avec les dates formatées
                        setLocalEmployee(prev => ({
                          ...prev,
                          date_embauche: formattedDateEmbauche,
                          date_naissance: formattedDateNaissance
                        }));

                        setIsEditing(true);
                      }}
                      className="flex items-center gap-2 bg-[#246990] hover:bg-[#246990]/90 text-white text-base px-5 py-2 h-auto rounded-lg"
                    >
                      <Edit size={18} />
                      Modifier
                    </Button>
                  )}
                </>
              )}

              {activeTab === 'documents' && (
                <div className="flex items-center text-emerald-600 bg-emerald-50 p-2 px-4 rounded-full">
                  <CheckCircle size={18} className="mr-2" />
                  <span className="text-sm font-medium">Les documents sont automatiquement sauvegardés</span>
                </div>
              )}

              <Button
                variant="outline"
                onClick={onClose}
                className="text-base px-5 py-2 h-auto"
              >
                Fermer
              </Button>
            </div>
          </Tabs.Root>
        </Dialog.Content>
      </Dialog.Portal>

      {/* Modal de réinitialisation de mot de passe */}
      <ResetPasswordModal
        isOpen={isResetPasswordModalOpen}
        onClose={() => setIsResetPasswordModalOpen(false)}
        onConfirm={handleResetPassword}
      />


      <DemandeAccesModal
        isOpen={isDemandeModalOpen}
        onClose={() => setIsDemandeModalOpen(false)}
        employee={localEmployee}
        typeDemande={typeDemande}
        onSubmit={handleSubmitDemande}
      />


      <ConfirmDialog
        isOpen={isConfirmDialogOpen}
        onClose={() => {
          setIsConfirmDialogOpen(false);
          setConfirmAction(null);
        }}
        onConfirm={handleConfirmAction}
        title="Confirmation"
        description="Confirmer que vous avez pris connaissance du refus et/ou contacté le support ?"
        confirmText="Oui, j'ai pris connaissance"
        cancelText="Annuler"
        customIcon={<CheckCircle size={32} />}
      />


      <ConfirmDialog
        isOpen={deleteConfirmDialog.isOpen}
        onClose={() => setDeleteConfirmDialog({
          isOpen: false,
          documentId: null,
          type: null
        })}
        onConfirm={executeDeleteDocument}
        title="Supprimer le document"
        description="Êtes-vous sûr de vouloir supprimer ce document ? Cette action est irréversible."
        confirmText="Supprimer"
        cancelText="Annuler"
        type="warning" // ou "error" pour une couleur rouge
      />


      {/* Dialog de confirmation pour téléchargement - AJOUTEZ CECI */}
      <ConfirmDialog
        isOpen={downloadConfirmDialog.isOpen}
        onClose={() => setDownloadConfirmDialog({
          isOpen: false,
          documentId: null,
          fileName: null
        })}
        onConfirm={executeDownloadDocument}
        title="Télécharger le document"
        description={`Voulez-vous télécharger le document "${downloadConfirmDialog.fileName}" ?`}
        confirmText="Télécharger"
        cancelText="Annuler"
        type="info"
        customIcon={<Download size={32} />}
      />

      {/* Dialog de confirmation de suppression d'enfant */}
      <ConfirmDialog
        isOpen={deleteEnfantDialog.isOpen}
        onClose={() => setDeleteEnfantDialog({ isOpen: false, enfantId: null, nomEnfant: '' })}
        onConfirm={executeDelierEnfant}
        title="Supprimer la liaison"
        description={`Êtes-vous sûr de vouloir supprimer la liaison avec ${deleteEnfantDialog.nomEnfant} ? Cette action est irréversible.`}
        confirmText="Supprimer"
        cancelText="Annuler"
        type="warning"
      />

    </Dialog.Root>
  );
};

export default EmployeeDetailModal;